import React, { useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';
import { barrierApi } from '../../services/api';
import toast from 'react-hot-toast';
import { useT } from '../../i18n';
import { formatDate } from '../../utils/date';

// Alias helpers — Baku time (UTC+4)
function fmtDT(dt) { return formatDate(dt, 'datetimesec'); }
function fmtDate(dt) { return formatDate(dt, 'datetime'); }
function fmtShort(dt) { return formatDate(dt, 'compact'); }

const METHOD_CFG = {
  internet:  { label: '🌐 İnternet', color: '#10b981' },
  bluetooth: { label: '📶 Bluetooth', color: '#3b82f6' },
  call:      { label: '📞 Zəng',     color: '#f59e0b' },
  guest_qr:  { label: '🔑 Qonaq QR', color: '#a78bfa' },
  scheduled: { label: '⏰ Cədvəl',   color: '#06b6d4' },
  system:    { label: '⚙️ Sistem',   color: '#94a3b8' },
  manual:    { label: '🖐 Manual',   color: '#fb923c' },
  app:       { label: '📱 App',      color: '#8b5cf6' },
  wifi:      { label: '📡 WiFi',     color: '#06b6d4' },
};

function exportLogsCsv(logs, barrierName) {
  const rows = [['Tarix', 'Metod', 'İstifadəçi', 'Telefon', 'Nəticə', 'Səbəb']];
  logs.forEach(l => {
    rows.push([
      fmtDT(l.createdAt), l.method || '',
      l.user?.fullName || 'Admin', l.user?.phone || '',
      l.success ? 'Uğurlu' : 'Uğursuz',
      l.failReason || '',
    ]);
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `${barrierName || 'barrier'}-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

function playOfflineSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(440, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.45);
  } catch { /* AudioContext not supported */ }
}
const EMPTY_FORM = { name: '', address: '', deviceSerial: '', notes: '', closeMode: 'timer', autoCloseSeconds: '4' };

function formatMac(raw) {
  const hex = raw.replace(/[^0-9A-Fa-f]/g, '').toUpperCase().slice(0, 12);
  return (hex.match(/.{1,2}/g) || []).join(':');
}
function MacInput({ value, onChange, style }) {
  return (
    <input style={style} value={value} maxLength={17} spellCheck={false}
      placeholder="AA:BB:CC:DD:EE:FF"
      onChange={e => onChange(formatMac(e.target.value))}
      onKeyDown={e => {
        if ((e.key === 'Backspace' || e.key === 'Delete') && value.endsWith(':')) {
          e.preventDefault(); onChange(value.slice(0, -1));
        }
      }} />
  );
}

/* ─────────────────────────────────────────────────────────────
   ANİMASİYALI ŞLAQBAUM SVG
   SVG özü position:absolute inset:0 — parent konteyneri ölçür
───────────────────────────────────────────────────────────── */
function BarrierVisual({ isOpen }) {
  const arm = isOpen ? -82 : 0;
  return (
    <svg
      viewBox="0 0 900 205"
      preserveAspectRatio="xMidYMid meet"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <defs>
        {/* Sunset / dusk sky */}
        <linearGradient id="bvSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#08051a"/>
          <stop offset="30%"  stopColor="#1c0f4a"/>
          <stop offset="58%"  stopColor="#6b21a8" stopOpacity="0.85"/>
          <stop offset="78%"  stopColor="#c2410c" stopOpacity="0.9"/>
          <stop offset="100%" stopColor="#fb923c" stopOpacity="0.75"/>
        </linearGradient>
        {/* Horizon glow */}
        <radialGradient id="bvHorizon" cx="50%" cy="100%" r="55%">
          <stop offset="0%"   stopColor="#f97316" stopOpacity="0.45"/>
          <stop offset="60%"  stopColor="#7c3aed" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="transparent"/>
        </radialGradient>
        {/* Road */}
        <linearGradient id="bvRoad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1a2535"/>
          <stop offset="100%" stopColor="#070e1c"/>
        </linearGradient>
        {/* Headlight cone */}
        <linearGradient id="bvBeam" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#fef3c7" stopOpacity="0.28"/>
          <stop offset="100%" stopColor="#fef3c7" stopOpacity="0"/>
        </linearGradient>
        {/* Car body */}
        <linearGradient id="bvCar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2563eb"/>
          <stop offset="100%" stopColor="#1e3a8a"/>
        </linearGradient>
        {/* Pole */}
        <linearGradient id="bvPole" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#0d1926"/>
          <stop offset="42%"  stopColor="#2d4a6e"/>
          <stop offset="100%" stopColor="#0d1926"/>
        </linearGradient>
        {/* Arm */}
        <linearGradient id="bvArm" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f1f5f9"/>
          <stop offset="100%" stopColor="#7a99b8"/>
        </linearGradient>
        {/* Reflective road stripe */}
        <linearGradient id="bvStripe" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1e3a5f" stopOpacity="0.7"/>
          <stop offset="100%" stopColor="#0d1e30" stopOpacity="0.3"/>
        </linearGradient>

        <filter id="bvGlw">
          <feGaussianBlur stdDeviation="3.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="bvSoft"><feGaussianBlur stdDeviation="7"/></filter>
        <filter id="bvDrop">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.55"/>
        </filter>
        <filter id="bvOrange">
          <feGaussianBlur stdDeviation="5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ── SKY ── */}
      <rect width="900" height="240" fill="url(#bvSky)"/>
      <rect width="900" height="240" fill="url(#bvHorizon)"/>

      {/* Stars */}
      {[22,70,130,200,280,370,455,540,630,710,800,870,50,160,260,380,500,640,755,845,100,310,480,720].map((x,i)=>(
        <circle key={i} cx={x} cy={4+(i%8)*11} r={0.45+(i%3)*0.35} fill="#c4b5fd"
          opacity={0.25+(i%4)*0.14}/>
      ))}

      {/* Moon */}
      <circle cx="790" cy="34" r="20" fill="#fef9c3" opacity="0.92"/>
      <circle cx="799" cy="28" r="15" fill="#1c0f4a" opacity="0.88"/>
      {/* Moon halo */}
      <circle cx="790" cy="34" r="28" fill="none" stroke="#fef3c7" strokeWidth="1" opacity="0.12"/>

      {/* Wispy clouds */}
      <ellipse cx="120" cy="52" rx="80"  ry="13" fill="#4c1d95" opacity="0.28"/>
      <ellipse cx="175" cy="46" rx="55"  ry="9"  fill="#5b21b6" opacity="0.18"/>
      <ellipse cx="560" cy="42" rx="95"  ry="15" fill="#7c3aed" opacity="0.2"/>
      <ellipse cx="505" cy="35" rx="60"  ry="9"  fill="#6d28d9" opacity="0.14"/>
      <ellipse cx="680" cy="58" rx="70"  ry="11" fill="#c2410c" opacity="0.18"/>

      {/* ── MOUNTAINS ── */}
      <path d="M0,158 L55,100 L115,132 L175,82 L245,108 L300,70 L365,102 L425,64 L490,94 L555,68 L620,98 L675,76 L730,106 L788,80 L845,108 L900,88 L900,165 Z"
        fill="#0b0820" opacity="0.9"/>
      <path d="M0,168 L75,122 L155,148 L230,112 L305,136 L375,106 L450,128 L525,102 L595,124 L665,110 L735,130 L808,116 L900,134 L900,168 Z"
        fill="#100d25" opacity="0.92"/>

      {/* ── CITY LEFT ── */}
      <rect x="0"   y="110" width="50"  height="58" fill="#0c0820" opacity="0.96"/>
      <rect x="12"  y="88"  width="22"  height="80" fill="#0c0820" opacity="0.96"/>
      <rect x="42"  y="102" width="32"  height="66" fill="#0c0820" opacity="0.92"/>
      <rect x="78"  y="118" width="40"  height="50" fill="#0c0820" opacity="0.88"/>
      <rect x="84"  y="96"  width="20"  height="72" fill="#0c0820" opacity="0.9"/>
      <rect x="122" y="120" width="30"  height="48" fill="#0c0820" opacity="0.85"/>
      <rect x="154" y="108" width="22"  height="60" fill="#0c0820" opacity="0.82"/>
      {/* Windows — warm amber/orange */}
      {[[14,92],[14,106],[26,92],[26,106],[45,105],[45,118],[56,105],[56,118],
        [80,100],[80,113],[92,100],[92,113],[80,126],[92,126],
        [124,123],[135,123],[156,111],[156,124],[166,111],[166,124]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="4" height="4" rx="0.8"
          fill={i%4===0?'#fbbf24':i%4===1?'#fb923c':i%4===2?'#f59e0b':'#fcd34d'}
          opacity={0.55+(i%3)*0.15}/>
      ))}

      {/* ── CITY RIGHT ── */}
      <rect x="700" y="108" width="44"  height="60" fill="#0c0820" opacity="0.9"/>
      <rect x="732" y="86"  width="28"  height="82" fill="#0c0820" opacity="0.95"/>
      <rect x="763" y="114" width="38"  height="54" fill="#0c0820" opacity="0.9"/>
      <rect x="804" y="92"  width="26"  height="76" fill="#0c0820" opacity="0.95"/>
      <rect x="832" y="112" width="68"  height="56" fill="#0c0820" opacity="0.9"/>
      <rect x="852" y="88"  width="24"  height="80" fill="#0c0820" opacity="0.95"/>
      {/* Windows — purple tinted */}
      {[[702,111],[713,111],[702,124],[713,124],
        [735,90],[735,103],[748,90],[748,103],[735,116],[748,116],
        [765,117],[776,117],[765,130],[776,130],
        [807,95],[818,95],[807,108],[818,108],
        [836,115],[847,115],[855,91],[866,91],[855,104],[866,104]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="4" height="4" rx="0.8"
          fill={i%3===0?'#fbbf24':i%3===1?'#a78bfa':'#fb923c'}
          opacity={0.5+(i%3)*0.13}/>
      ))}

      {/* ── ROAD ── */}
      <rect x="0" y="167" width="900" height="73" fill="url(#bvRoad)"/>
      <rect x="0" y="164" width="900" height="5"  rx="1" fill="#091424"/>
      {/* Dashed lane markings */}
      {[0,1,2,3,4,5,6,7,8,9,10,11].map(i=>(
        <rect key={i} x={i*78+16} y="198" width="48" height="6" rx="2.5" fill="url(#bvStripe)"/>
      ))}
      {/* Road edge lines */}
      <rect x="0" y="170" width="900" height="2" rx="1" fill="#0e2034" opacity="0.5"/>

      {/* ── STREET LAMP ── */}
      <rect x="184" y="72" width="6" height="96" rx="3" fill="#0e1d2e"/>
      <path d="M184,76 Q170,65 160,68" stroke="#0e1d2e" strokeWidth="5" fill="none" strokeLinecap="round"/>
      <ellipse cx="159" cy="67" rx="11" ry="5" fill="#fef3c7" opacity="0.88"/>
      {/* Lamp glow cone */}
      <ellipse cx="159" cy="80" rx="28" ry="18" fill="#fef3c7" opacity="0.06" filter="url(#bvSoft)"/>

      {/* ── CAR (approaching from right, waiting at barrier) ── */}
      {/* Ground shadow */}
      <ellipse cx="720" cy="232" rx="72" ry="7" fill="#000" opacity="0.45"/>
      {/* Headlight beam */}
      <path d="M658,207 L900,178 L900,228 Z" fill="url(#bvBeam)"/>
      <ellipse cx="690" cy="207" rx="45" ry="13" fill="#fef3c7" opacity="0.06" filter="url(#bvSoft)"/>
      {/* Wheels */}
      <circle cx="666" cy="225" r="17" fill="#111827" stroke="#374151" strokeWidth="2.5"/>
      <circle cx="666" cy="225" r="8"  fill="#1f2937"/>
      <circle cx="666" cy="225" r="3"  fill="#374151"/>
      <circle cx="776" cy="225" r="17" fill="#111827" stroke="#374151" strokeWidth="2.5"/>
      <circle cx="776" cy="225" r="8"  fill="#1f2937"/>
      <circle cx="776" cy="225" r="3"  fill="#374151"/>
      {/* Car body */}
      <rect x="645" y="200" width="152" height="28" rx="6" fill="url(#bvCar)"/>
      {/* Roof */}
      <path d="M668,200 L683,172 L755,172 L775,200 Z" fill="#1d4ed8" opacity="0.96"/>
      {/* Windscreen */}
      <path d="M686,198 L696,175 L730,175 L730,198 Z" fill="#bfdbfe" opacity="0.68" rx="2"/>
      {/* Rear window */}
      <path d="M733,198 L733,175 L752,175 L770,198 Z" fill="#bfdbfe" opacity="0.58" rx="2"/>
      {/* Door line */}
      <line x1="730" y1="200" x2="730" y2="227" stroke="#1e40af" strokeWidth="1.2" opacity="0.55"/>
      <circle cx="732" cy="214" r="2.5" fill="#93c5fd" opacity="0.6"/>
      {/* Headlights */}
      <rect x="793" y="205" width="9" height="11" rx="2.5" fill="#fef9c3" opacity="0.95"/>
      <rect x="793" y="205" width="9" height="11" rx="2.5" fill="#fef9c3" filter="url(#bvGlw)"/>
      {/* Tail lights */}
      <rect x="643" y="207" width="6"  height="12" rx="2" fill="#ef4444" opacity="0.88"/>
      <rect x="643" y="207" width="6"  height="12" rx="2" fill="#ef4444" filter="url(#bvGlw)" opacity="0.4"/>
      {/* Side mirror */}
      <rect x="791" y="198" width="10" height="6" rx="2" fill="#1e3a8a"/>

      {/* ── BARRIER POLE ── */}
      <rect x="248" y="44" width="26" height="124" rx="4" fill="url(#bvPole)" filter="url(#bvDrop)"/>
      <rect x="234" y="162" width="56" height="9"   rx="3.5" fill="#091424"/>
      <rect x="244" y="38"  width="36" height="10"  rx="5"   fill="#162a40"/>

      {/* Signal box */}
      <rect x="222" y="10" width="72" height="42" rx="9" fill="#091424" stroke="#152a42" strokeWidth="1.5"/>
      <circle cx="246" cy="31" r="13"
        fill={isOpen ? '#162030' : '#dc2626'} filter={!isOpen ? 'url(#bvGlw)' : undefined}
        style={{ transition: 'fill 0.45s' }}/>
      <circle cx="276" cy="31" r="13"
        fill={isOpen ? '#10b981' : '#162030'} filter={isOpen ? 'url(#bvGlw)' : undefined}
        style={{ transition: 'fill 0.45s' }}/>

      {/* ── BARRIER ARM (pivot 274, 96) ── */}
      <g style={{
        transform: `translate(274px,96px) rotate(${arm}deg)`,
        transformOrigin: '0 0',
        transition: 'transform 1.05s cubic-bezier(0.34,1.2,0.64,1)',
      }}>
        {/* Counterweight */}
        <rect x="-62" y="-7"  width="62"  height="14" rx="7" fill="#1e3350"/>
        <rect x="-80" y="-11" width="20"  height="22" rx="6" fill="#263e5a"/>
        {/* Arm shadow layer */}
        <rect x="2"   y="-4"  width="360" height="9"  rx="5" fill="#000" opacity="0.22"/>
        {/* Arm body */}
        <rect x="0"   y="-7"  width="360" height="14" rx="7" fill="url(#bvArm)"/>
        {/* Red warning stripes */}
        {[0,1,2,3,4,5,6,7,8,9].map(i=>(
          <rect key={i} x={i*34+10} y="-7" width="22" height="14" rx="4" fill="#dc2626" opacity={0.88}/>
        ))}
        {/* Shine */}
        <rect x="6" y="-7" width="348" height="4" rx="3" fill="rgba(255,255,255,0.22)"/>
        {/* End cap */}
        <rect x="356" y="-12" width="6" height="24" rx="3" fill="#1e3350"/>
        {/* End light */}
        <circle cx="362" cy="0" r="10"
          fill={isOpen ? '#10b981' : '#dc2626'} filter="url(#bvGlw)"
          style={{ transition: 'fill 0.45s' }}/>
        <circle cx="362" cy="0" r="4" fill="rgba(255,255,255,0.25)"/>
      </g>

      {/* Pivot bolt */}
      <circle cx="274" cy="96" r="13" fill="#1e3350"/>
      <circle cx="274" cy="96" r="8"  fill="#2d4a6e"/>
      <circle cx="274" cy="96" r="3.5" fill="#7a99b8"/>

      {/* Right stop post */}
      <rect x="606" y="78" width="14" height="88" rx="4" fill="#091424" stroke="#152a42" strokeWidth="1"/>
      <rect x="600" y="160" width="26" height="8"  rx="3" fill="#091424"/>
      <rect x="608" y="100" width="10" height="8"  rx="2"
        fill={isOpen ? '#10b981' : '#dc2626'} opacity="0.85"
        style={{ transition: 'fill 0.45s' }}/>

      {/* Road reflections under lamp */}
      <ellipse cx="163" cy="175" rx="22" ry="5" fill="#fef3c7" opacity="0.06"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   QONAQ QR TAB KOMPONENTI
───────────────────────────────────────────────────────────── */
function GuestQrTab({ barrierId, S }) {
  const t = useT();
  const EMPTY_GQ = { guestName: '', guestPhone: '', maxUses: '1', expiresHours: '24', note: '' };
  const [guestQrs, setGuestQrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_GQ);

  useEffect(() => {
    if (!barrierId) return;
    setLoading(true);
    barrierApi.listGuestQrs(barrierId)
      .then(data => setGuestQrs(Array.isArray(data) ? data : []))
      .catch(() => setGuestQrs([]))
      .finally(() => setLoading(false));
  }, [barrierId]);

  const handleCreate = async e => {
    e.preventDefault();
    if (!barrierId) return;
    setSaving(true);
    try {
      const created = await barrierApi.createGuestQr(barrierId, {
        guestName: form.guestName || undefined,
        guestPhone: form.guestPhone || undefined,
        maxUses: parseInt(form.maxUses) || 1,
        expiresHours: parseInt(form.expiresHours) || 24,
        note: form.note || undefined,
      });
      setGuestQrs(prev => [created, ...prev]);
      setForm(EMPTY_GQ);
      toast.success('QR yaradıldı');
    } catch {
      toast.error('QR yaradıla bilmədi');
    } finally {
      setSaving(false);
    }
  };

  const copyToken = token => {
    navigator.clipboard.writeText(token).then(() => toast.success('Kopyalandı'));
  };
  const shareToken = token => {
    navigator.clipboard.writeText(`Bakı Gate QR: ${token}`).then(() => toast.success('Paylaşım kopyalandı'));
  };

  const fmtExpiry = expiresAt => {
    if (!expiresAt) return '—';
    const diff = new Date(expiresAt) - Date.now();
    if (diff <= 0) return 'Müddəti bitmişdir';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}s ${m}d qalıb` : `${m}d qalıb`;
  };

  const inp = { ...S.inp };
  const label = { fontSize: 11, color: 'var(--text3)', marginBottom: 3 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Create form */}
      <form onSubmit={handleCreate} style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>{t('bar_new_guest_qr')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <div style={label}>{t('bar_guest_name')}</div>
            <input style={inp} placeholder="Əli Həsənov" value={form.guestName}
              onChange={e => setForm({ ...form, guestName: e.target.value })} />
          </div>
          <div>
            <div style={label}>{t('bar_guest_phone')}</div>
            <input style={inp} type="tel" placeholder="+994XXXXXXXXX" value={form.guestPhone}
              onChange={e => setForm({ ...form, guestPhone: e.target.value })} />
          </div>
          <div>
            <div style={label}>{t('bar_guest_max_uses')}</div>
            <input style={inp} type="number" min="1" max="10" value={form.maxUses}
              onChange={e => setForm({ ...form, maxUses: e.target.value })} />
          </div>
          <div>
            <div style={label}>{t('bar_guest_expires')}</div>
            <select style={inp} value={form.expiresHours}
              onChange={e => setForm({ ...form, expiresHours: e.target.value })}>
              <option value="1">1 {t('bar_hour')}</option>
              <option value="6">6 {t('bar_hour')}</option>
              <option value="24">24 {t('bar_hour')}</option>
              <option value="72">3 {t('bar_day')}</option>
              <option value="168">7 {t('bar_day')}</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={label}>{t('notes')} ({t('bar_optional_short')})</div>
          <input style={inp} placeholder={t('bar_guest_note_ph')} value={form.note}
            onChange={e => setForm({ ...form, note: e.target.value })} />
        </div>
        <button style={{ ...S.btn, width: '100%' }} type="submit" disabled={saving}>
          {saving ? '…' : '🔑 ' + t('bar_create_qr')}
        </button>
      </form>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading
          ? <div style={S.hint}>{t('loading')}</div>
          : guestQrs.length === 0
            ? <div style={S.emptyList}>{t('bar_no_guest_qr')}</div>
            : guestQrs.map(qr => (
                <div key={qr.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <code style={{ flex: 1, fontSize: 11, fontFamily: 'monospace', background: 'var(--card)', padding: '4px 8px', borderRadius: 6, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', border: '1px solid var(--border)' }}
                      onClick={() => copyToken(qr.token)} title="Kopyala">
                      {qr.token}
                    </code>
                    <button style={{ padding: '4px 8px', fontSize: 11, background: '#0f1e30', border: '1px solid #1a2d48', borderRadius: 6, cursor: 'pointer', color: '#64748b' }}
                      onClick={() => copyToken(qr.token)} title="Kopyala">⎘</button>
                    <button style={{ padding: '4px 8px', fontSize: 11, background: '#0f1e30', border: '1px solid #1a2d48', borderRadius: 6, cursor: 'pointer', color: '#64748b' }}
                      onClick={() => shareToken(qr.token)} title="Paylaş">↗</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11, alignItems: 'center' }}>
                    {qr.guestName && <span style={{ color: 'var(--text)', fontWeight: 600 }}>{qr.guestName}</span>}
                    <span style={{ color: 'var(--text3)' }}>{fmtExpiry(qr.expiresAt)}</span>
                    <span style={{ color: 'var(--text3)' }}>{qr.usedCount || 0}/{qr.maxUses} {t('bar_uses')}</span>
                    <span style={{
                      padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                      background: qr.isActive ? '#10b98118' : '#ef444418',
                      color: qr.isActive ? '#10b981' : '#ef4444',
                      border: `1px solid ${qr.isActive ? '#10b98130' : '#ef444430'}`,
                    }}>{qr.isActive ? t('active') : t('inactive')}</span>
                  </div>
                </div>
              ))
        }
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ANA KOMPONENT
───────────────────────────────────────────────────────────── */
export default function Barriers() {
  const t = useT();
  const [barriers,      setBarriers]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selected,      setSelected]      = useState(null);
  const [detailTab,     setDetailTab]     = useState('logs');
  const [logs,          setLogs]          = useState([]);
  const [users,         setUsers]         = useState([]);
  const [logsLoading,   setLogsLoading]   = useState(false);
  const [usersLoading,  setUsersLoading]  = useState(false);
  const [showCreate,    setShowCreate]    = useState(false);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [editMode,      setEditMode]      = useState(false);
  const [editForm,      setEditForm]      = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [addPhone,      setAddPhone]      = useState('');
  const [opening,       setOpening]       = useState(null);
  const [copied,        setCopied]        = useState(false);
  const [barrierOpen,   setBarrierOpen]   = useState(false);
  const [countdown,     setCountdown]     = useState(0);
  const [showCfg,       setShowCfg]       = useState(false);
  const [cfgForm,       setCfgForm]       = useState({
    wifiSsid:'', wifiPassword:'', serverHost:'', serverPort:'3000',
    closeMode:'timer', autoCloseSeconds:'4',
    heartbeatMs:'30', sensorTimeoutMs:'60',
    gsmApn:'internet', gsmUser:'', gsmPass:'',
    simPhone:'',
    servoOpen:'90', servoClosed:'0', servoSpeed:'0',
    bleEnabled: false, bleRssiThreshold:'-70', bleScanDuration:'5', bleAutoOpen: false, bleOpenDelay:'0',
  });
  const [cfgSending,    setCfgSending]    = useState(false);
  const [cfgStatus,     setCfgStatus]     = useState('');
  const [cfgProgress,   setCfgProgress]   = useState(0);   // 0-100 — real-time göndərmə faizi
  const [cfgSnap,       setCfgSnap]       = useState(null);
  // OTA state
  const [otaProgress,   setOtaProgress]   = useState(0);    // 0-100
  const [otaStatus,     setOtaStatus]     = useState('');   // mesaj
  const [otaActive,     setOtaActive]     = useState(false);
  const [fwInfo,        setFwInfo]        = useState(null); // server firmware meta
  const [fwUploading,   setFwUploading]   = useState(false);
  const [fwVersion,     setFwVersion]     = useState('');   // upload forması
  const [logFilter,     setLogFilter]     = useState('all');
  const [relayTesting,  setRelayTesting]  = useState(false);
  const [relayCountdown,setRelayCountdown]= useState(0);
  const relayTimerRef = useRef(null);
  const fwFileRef = useRef(null);

  const timerRef     = useRef(null);
  const cfgToRef     = useRef(null);   // konfiq timeout ref
  const socketRef    = useRef(null);
  const otaSimRef    = useRef(null);  // simulyasiya interval ref
  const selectedRef  = useRef(null);   // WS callback üçün closure-safe ref

  // selectedRef həmişə aktual selected-i saxlayır
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // Modal açmaq — cihazın beynindəki bütün məlumatlar anında prefill olunur
  const handleOpenCfg = useCallback(() => {
    if (!selected) return;
    const snap = { ...selected };
    setCfgSnap(snap);
    setCfgForm({
      wifiSsid:         snap.lastKnownSsid          || '',
      wifiPassword:     snap.lastKnownWifiPass       || '',
      serverHost:       snap.lastKnownServerHost     || '',
      serverPort:       '3000',
      closeMode:        snap.closeMode               || 'timer',
      autoCloseSeconds: String(snap.autoCloseSeconds  ?? 4),
      heartbeatMs:      String((snap.lastKnownHeartbeatMs  || 30000) / 1000),
      sensorTimeoutMs:  String((snap.lastKnownSensorTimeout || 60000) / 1000),
      gsmApn:           snap.lastKnownGsmApn         || 'internet',
      gsmUser:          snap.lastKnownGsmUser        || '',
      gsmPass:          snap.lastKnownGsmPass        || '',
      simPhone:         snap.simPhone                || '',
      servoOpen:          String(snap.servoOpenAngle   ?? 90),
      servoClosed:        String(snap.servoClosedAngle ?? 0),
      servoSpeed:         String(snap.servoSpeed       ?? 0),
      bleEnabled:         snap.bleEnabled          ?? false,
      bleRssiThreshold:   String(snap.bleRssiThreshold ?? -70),
      bleScanDuration:    String(snap.bleScanDuration  ?? 5),
      bleAutoOpen:        snap.bleAutoOpen         ?? false,
      bleOpenDelay:       String(snap.bleOpenDelay     ?? 0),
    });
    setCfgStatus('');
    setCfgProgress(0);
    setCfgSending(false);
    if (cfgToRef.current) clearTimeout(cfgToRef.current);
    setShowCfg(true);
  }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await barrierApi.list(); setBarriers(Array.isArray(d) ? d : []); }
    catch { toast.error('Yüklənmədi'); }
    finally { setLoading(false); }
  }, []);

  // ── Socket.IO — real-time yeniləmələr ────────────────────────
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
    const serverUrl = apiBase.replace('/api/v1', '');
    const socket = io(serverUrl + '/barriers', {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;
    // 'admin' otağına qoşul — reconnect-dən sonra da yenilənir
    socket.on('connect', () => socket.emit('joinAdmin'));

    // Barrier status yeniləndi (heartbeat / LWT / elektrik...)
    socket.on('statusChange', data => {
      const { barrierId, ...fields } = data;
      if (fields.isOnline === false) {
        playOfflineSound();
        toast.error(`Şlaqbaum offline oldu`, { icon: '⚠️', duration: 4000 });
      }
      setBarriers(prev => prev.map(b => b.id === barrierId ? { ...b, ...fields } : b));
      setSelected(prev => prev?.id === barrierId ? { ...prev, ...fields } : prev);
    });

    // Cihaz konfiqurasyonu tətbiq etdi → progress 100%
    socket.on('configApplied', data => {
      if (selectedRef.current?.id === data.barrierId) {
        if (cfgToRef.current) clearTimeout(cfgToRef.current);
        setCfgProgress(100);
        setCfgStatus('✅ Cihaz tətbiq etdi!');
        setCfgSending(false);
        toast.success('Konfiq cihaz tərəfindən tətbiq edildi!');
      }
    });

    socket.on('accessEvent', data => {
      if (selectedRef.current?.id === data.barrierId) {
        setLogs(prev => [{ ...data, id: Date.now() }, ...prev].slice(0, 100));
      }
    });

    // OTA progress (0-100) — cihazdan MQTT üzərindən gəlir
    socket.on('otaProgress', data => {
      if (selectedRef.current?.id === data.barrierId) {
        const pct = data.percent ?? 0;
        setOtaProgress(prev => {
          // Əgər real progress simulyasiyanı keçibsə, simulyasiyanı dayandır
          if (pct > prev && otaSimRef.current) {
            clearInterval(otaSimRef.current);
            otaSimRef.current = null;
          }
          return pct;
        });
        setOtaStatus(`📥 Endirilir... ${pct}%`);
      }
    });

    // OTA status (started / complete / error)
    socket.on('otaStatus', data => {
      if (selectedRef.current?.id === data.barrierId) {
        if (data.status === 'started') {
          // Cihaz OTA aldığını təsdiqlədi — gözləmə counter-ini sil
          if (otaSimRef.current) { clearInterval(otaSimRef.current); otaSimRef.current = null; }
          setOtaActive(true);
          setOtaProgress(5);
          setOtaStatus('📥 Cihaz firmware endirir...');

          // MQTT progress gəlməsə belə progress bar irəliləsin (maks 85%-ə qədər)
          let simPct = 5;
          otaSimRef.current = setInterval(() => {
            simPct = Math.min(85, simPct + 1);
            setOtaProgress(prev => {
              if (prev < simPct) return simPct;
              if (prev >= 85 || prev > simPct + 5) {
                clearInterval(otaSimRef.current);
                otaSimRef.current = null;
              }
              return prev;
            });
            if (simPct >= 85) {
              clearInterval(otaSimRef.current);
              otaSimRef.current = null;
            }
          }, 800);

        } else if (data.status === 'complete') {
          if (otaSimRef.current) { clearInterval(otaSimRef.current); otaSimRef.current = null; }
          setOtaActive(false);
          setOtaProgress(100);
          setOtaStatus(`✅ Uğurlu! Yeni versiya: v${data.version}`);
          toast.success(`Firmware v${data.version} yükləndi!`);
          setBarriers(prev => prev.map(b => b.id === data.barrierId
            ? { ...b, firmwareVersion: data.version } : b));
        } else if (data.status === 'error') {
          if (otaSimRef.current) { clearInterval(otaSimRef.current); otaSimRef.current = null; }
          setOtaActive(false);
          setOtaProgress(0);
          setOtaStatus(`❌ Xəta: ${data.error}`);
          toast.error('OTA xətası: ' + data.error);
        }
      }
    });

    return () => {
      socket.disconnect();
      if (relayTimerRef.current) clearInterval(relayTimerRef.current);
    };
  }, []); // eslint-disable-line

  // Barrier seçiləndə həmin otağa qoşul
  useEffect(() => {
    if (selected?.id && socketRef.current) {
      socketRef.current.emit('watch', selected.id);
    }
  }, [selected?.id]); // eslint-disable-line

  // ── Initial load + 30s polling (WS əlaqəsi kəsildikdə ehtiyat) ──
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      barrierApi.list()
        .then(d => { if (Array.isArray(d)) setBarriers(d); })
        .catch(() => {});
    }, 30000); // WS-dən sonra 30s kifayətdir
    return () => clearInterval(iv);
  }, [load]); // eslint-disable-line

  const resetAnim = useCallback(() => {
    setBarrierOpen(false); setCountdown(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const openDetail = useCallback((b, tab = 'logs') => {
    setSelected(b); setDetailTab(tab); setEditMode(false); resetAnim();
    loadLogs(b.id); loadUsers(b.id);
  }, [resetAnim]); // eslint-disable-line

  const loadLogs  = async id => {
    setLogsLoading(true);
    try {
      const d = await barrierApi.logs(id);
      // Backend returns {data:[...], total, pages} OR plain array
      const arr = Array.isArray(d) ? d : (d?.data || []);
      setLogs(arr);
    }
    catch { setLogs([]); } finally { setLogsLoading(false); }
  };
  const loadUsers = async id => {
    setUsersLoading(true);
    try { const d = await barrierApi.getUsers(id); setUsers(Array.isArray(d) ? d : []); }
    catch { setUsers([]); } finally { setUsersLoading(false); }
  };

  /* CRUD */
  const handleCreate = async e => {
    e.preventDefault(); if (!form.name.trim()) return;
    try {
      const c = await barrierApi.create({ name: form.name.trim(), address: form.address.trim() || undefined, deviceSerial: form.deviceSerial.trim() || undefined, notes: form.notes.trim() || undefined });
      toast.success('Əlavə edildi'); setShowCreate(false); setForm(EMPTY_FORM);
      await load(); if (c?.id) openDetail(c, 'info');
    } catch { toast.error('Xəta'); }
  };

  const startEdit = () => {
    setEditForm({
      name: selected.name||'', address: selected.address||'',
      deviceSerial: selected.deviceSerial||'', notes: selected.notes||'',
      closeMode: selected.closeMode||'timer', autoCloseSeconds: String(selected.autoCloseSeconds??4),
    });
    setEditMode(true); setDetailTab('info');
  };

  const handleSave = async e => {
    e.preventDefault(); if (!editForm.name.trim()) { toast.error('Ad boş ola bilməz'); return; }
    setSaving(true);
    try {
      const u = await barrierApi.update(selected.id, {
        name: editForm.name.trim(), address: editForm.address.trim()||undefined,
        deviceSerial: editForm.deviceSerial.trim()||undefined, notes: editForm.notes.trim()||undefined,
        closeMode: editForm.closeMode, autoCloseSeconds: parseInt(editForm.autoCloseSeconds)||4,
      });
      toast.success('Saxlanıldı'); setSelected(u); setBarriers(p => p.map(b => b.id===u.id ? u : b)); setEditMode(false);
    } catch { toast.error('Xəta'); } finally { setSaving(false); }
  };

  const handleSendConfig = async e => {
    e.preventDefault();
    if (cfgSending) return;
    const f = cfgForm;

    // Göndəriləcək payload — yalnız dolu sahələr
    const payload = {};
    if (f.wifiSsid.trim())      payload.wifiSsid         = f.wifiSsid.trim();
    if (f.wifiPassword.trim())  payload.wifiPassword     = f.wifiPassword.trim();
    if (f.serverHost.trim())    payload.serverHost       = f.serverHost.trim();
    if (f.serverPort)           payload.serverPort       = parseInt(f.serverPort) || 3000;
    if (f.closeMode)            payload.closeMode        = f.closeMode;
    if (f.autoCloseSeconds)     payload.autoCloseSeconds = parseInt(f.autoCloseSeconds) || 4;
    // heartbeatMs və sensorTimeoutMs formda SANİYƏ olaraq saxlanır — ms-ə çevir
    if (f.heartbeatMs)          payload.heartbeatMs      = (parseInt(f.heartbeatMs) || 30) * 1000;
    if (f.sensorTimeoutMs)      payload.sensorTimeoutMs  = (parseInt(f.sensorTimeoutMs) || 60) * 1000;
    if (f.gsmApn.trim())        payload.gsmApn           = f.gsmApn.trim();
    if (f.gsmUser.trim())       payload.gsmUser          = f.gsmUser.trim();
    if (f.gsmPass.trim())       payload.gsmPass          = f.gsmPass.trim();
    if (f.simPhone.trim())      payload.simPhone         = f.simPhone.trim();
    if (f.servoOpen !== '')     payload.servoOpenAngle   = parseInt(f.servoOpen)   || 90;
    if (f.servoClosed !== '')   payload.servoClosedAngle = parseInt(f.servoClosed) || 0;
    if (f.servoSpeed !== '')    payload.servoSpeed       = parseInt(f.servoSpeed)  || 0;
    payload.bleEnabled        = !!f.bleEnabled;
    payload.bleRssiThreshold  = parseInt(f.bleRssiThreshold) || -70;
    payload.bleScanDuration   = parseInt(f.bleScanDuration)  || 5;
    payload.bleAutoOpen       = !!f.bleAutoOpen;
    payload.bleOpenDelay      = parseInt(f.bleOpenDelay)     || 0;

    setCfgSending(true);
    setCfgProgress(10);
    setCfgStatus('📡 Server-ə göndərilir...');

    try {
      await barrierApi.sendConfig(selected.id, payload);

      setCfgProgress(40);
      setCfgStatus('📶 MQTT vasitəsilə cihaza çatdırılır...');

      // Məlum dəyərləri DB snapshot-undan yenilə (WS heartbeat-dən əvvəl)
      setSelected(prev => ({ ...prev, ...payload }));
      setBarriers(bs => bs.map(b => b.id === selected.id ? { ...b, ...payload } : b));

      // 3s sonra progress artır — cihaz cavabı gözlənilir
      cfgToRef.current = setTimeout(() => {
        setCfgProgress(65);
        setCfgStatus('⏳ Cihazın tətbiqini gözlənilir...');

        // WiFi/Server dəyişibsə cihaz yenidən başlayır — 20s əlavə timeout
        const hasNetChange = !!(payload.wifiSsid || payload.wifiPassword || payload.serverHost);
        cfgToRef.current = setTimeout(() => {
          setCfgProgress(prev => prev < 100 ? 85 : prev);
          setCfgStatus(hasNetChange
            ? '🔄 Cihaz yenidən başlayır (WiFi/Server dəyişdi)... 30s gözləyin'
            : '⏳ Cihaz hələ cavab vermədi — növbəti heartbeat-də tətbiq olunacaq'
          );
          setCfgSending(false);
        }, hasNetChange ? 20000 : 15000);
      }, 3000);

    } catch(err) {
      if (cfgToRef.current) clearTimeout(cfgToRef.current);
      setCfgProgress(0);
      setCfgStatus('❌ Xəta: ' + (err?.message || 'Server cavab vermədi'));
      toast.error(err?.message || 'Göndərmə xətası');
      setCfgSending(false);
    }
  };

  // Arduino IDE-nin export etdiyi .bin faylı lokal qovluqdan götür
  const handleLoadLocal = async () => {
    setFwUploading(true);
    try {
      const meta = await barrierApi.loadLocalFirmware(fwVersion || undefined);
      setFwInfo(meta);
      toast.success(`v${meta.version} (${(meta.size/1024).toFixed(0)} KB) lokal qovluqdan yükləndi!`);
      setFwVersion('');
    } catch(e) {
      toast.error(e?.message || 'Lokal fayl tapılmadı');
    } finally { setFwUploading(false); }
  };

  // Firmware server-ə yüklə
  const handleFwUpload = async () => {
    const file = fwFileRef.current?.files?.[0];
    if (!file) { toast.error('.bin fayl seçin'); return; }
    setFwUploading(true);
    try {
      const meta = await barrierApi.uploadFirmware(file, fwVersion || undefined);
      setFwInfo(meta);
      toast.success(`Firmware v${meta.version} server-ə yükləndi!`);
      if (fwFileRef.current) fwFileRef.current.value = '';
      setFwVersion('');
    } catch(e) {
      toast.error(e?.message || 'Yükləmə xətası');
    } finally { setFwUploading(false); }
  };

  // Cihaza OTA koandası göndər
  const handleTriggerOta = async () => {
    if (!selected) return;
    if (!fwInfo) { toast.error('Əvvəlcə firmware server-ə yükləyin'); return; }
    if (!selected.isOnline) {
      toast.error('Cihaz offline — OTA mümkün deyil');
      return;
    }
    if (!window.confirm(`"${selected.name}" cihazına v${fwInfo.version} firmware göndərilsin?`)) return;

    // Köhnə simulyasiyanı təmizlə
    if (otaSimRef.current) { clearInterval(otaSimRef.current); otaSimRef.current = null; }
    setOtaActive(true);
    setOtaProgress(0);
    setOtaStatus('📡 Komanda göndərilir...');
    try {
      await barrierApi.triggerOta(selected.id);
      // Komanda MQTT-ə göndərildi. İndi cihazın "ota_start" cavabını gözlə.
      // Cihaz cavablamazsa 20s sonra timeout göstər.
      setOtaStatus('⏳ Cihaz cavabı gözlənilir...');
      if (otaSimRef.current) clearInterval(otaSimRef.current);
      let waitSec = 0;
      otaSimRef.current = setInterval(() => {
        waitSec++;
        setOtaProgress(0); // sıfırda saxla — cihaz ota_start göndərməyib
        setOtaStatus(`⏳ Cihaz cavabı gözlənilir... (${waitSec}s)`);
        if (waitSec >= 20) {
          clearInterval(otaSimRef.current);
          otaSimRef.current = null;
          setOtaActive(false);
          setOtaProgress(0);
          setOtaStatus('⏱ Cihaz cavab vermədi — MQTT bağlantısını yoxlayın');
        }
      }, 1000);
    } catch(e) {
      if (otaSimRef.current) { clearInterval(otaSimRef.current); otaSimRef.current = null; }
      setOtaActive(false);
      setOtaProgress(0);
      setOtaStatus('❌ ' + (e?.message || 'Xəta'));
      toast.error(e?.message || 'OTA xətası');
    }
  };

  // Firmware məlumatlarını yüklə
  const loadFwInfo = async () => {
    try { const d = await barrierApi.firmwareInfo(); setFwInfo(d?.uploaded === false ? null : d); }
    catch { setFwInfo(null); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Bu şlaqbaumu silmək istəyirsiniz?')) return;
    try {
      await barrierApi.deleteBarrier(id);
      toast.success('Şlaqbaum silindi');
      if (selected?.id === id) { setSelected(null); resetAnim(); }
      await load();
    } catch(e) {
      toast.error(e?.message || 'Silinmə zamanı xəta baş verdi');
    }
  };

  /* OPEN — yalnız API uğurlu olduqdan sonra animasiya başlayır */
  const handleOpen = async id => {
    const mine = id === selected?.id;
    // Offline cihaza əmr göndərməyə çalışma
    const b = barriers.find(x => x.id === id);
    if (b) {
      const mins = b.lastSeenAt ? (Date.now() - new Date(b.lastSeenAt)) / 60000 : null;
      const devOnline = b.isOnline && mins !== null && mins < 2;
      if (!devOnline) { toast.error('Cihaz offline-dir — əmr göndərmək olmur'); return; }
    }
    setOpening(id);
    try {
      await barrierApi.adminOpen(id);
      toast.success('Şlaqbaum açıldı!');
      if (mine) {
        const sec = selected?.autoCloseSeconds || 4;
        setBarrierOpen(true); setCountdown(sec);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
          setCountdown(c => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
        }, 1000);
        setTimeout(() => { setBarrierOpen(false); setCountdown(0); }, sec * 1000);
        loadLogs(id);
      }
    } catch(e) {
      toast.error(e?.message || 'Cihaz cavab vermədi');
      if (mine) { setBarrierOpen(false); setCountdown(0); if (timerRef.current) clearInterval(timerRef.current); }
    } finally { setOpening(null); }
  };

  const handleRelayTest = async () => {
    if (!selected || relayTesting) return;
    const b = barriers.find(x => x.id === selected.id);
    const mins = b?.lastSeenAt ? (Date.now() - new Date(b.lastSeenAt)) / 60000 : null;
    if (!b?.isOnline || mins === null || mins >= 1.5) { toast.error('Cihaz offline — relay test mümkün deyil'); return; }
    setRelayTesting(true);
    const sec = selected?.autoCloseSeconds || 4;
    setRelayCountdown(sec);
    try {
      await barrierApi.adminOpen(selected.id);
      toast.success('🔌 Relay test aktivdir!');
      if (relayTimerRef.current) clearInterval(relayTimerRef.current);
      relayTimerRef.current = setInterval(() => {
        setRelayCountdown(c => {
          if (c <= 1) { clearInterval(relayTimerRef.current); setRelayTesting(false); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch(e) {
      toast.error(e?.message || 'Relay test xətası');
      setRelayTesting(false); setRelayCountdown(0);
    }
  };

  const handleAddUser = async e => {
    e.preventDefault(); if (!addPhone.trim()) return;
    try { await barrierApi.addUser(selected.id, addPhone.trim()); toast.success('Əlavə edildi'); setAddPhone(''); loadUsers(selected.id); }
    catch(e) { toast.error(e?.message || 'Tapılmadı'); }
  };
  const handleRemoveUser = async uid => {
    try { await barrierApi.removeUser(selected.id, uid); toast.success('Çıxarıldı'); loadUsers(selected.id); }
    catch { toast.error('Xəta'); }
  };

  const buildFirmwareConfig = b => `// BAKU GATE — ESP32 Firmware Config
// Barrier: ${b.name}
// MAC: ${b.deviceSerial || '(daxil edilməyib)'}
// MQTT: ${b.mqttTopic || '(yoxdur)'}

const char* WIFI_SSID     = "WIFI_ADINI_YAZIN";
const char* WIFI_PASSWORD = "WIFI_SIFRENI_YAZIN";
const char* SERVER_HOST   = "SERVER_IP";     // məs: 192.168.1.100
const int   SERVER_PORT   = 3000;
// Server endpoint: http://SERVER_HOST:3000/api/v1/barriers/provision

// ── BLE Ayarları ─────────────────────────────────────
const bool BLE_ENABLED         = ${b.bleEnabled ? 'true' : 'false'};
const int  BLE_RSSI_THRESHOLD  = ${b.bleRssiThreshold ?? -70};  // dBm — minimum siqnal gücü
const int  BLE_SCAN_DURATION   = ${b.bleScanDuration ?? 5};     // saniyə
const bool BLE_AUTO_OPEN       = ${b.bleAutoOpen ? 'true' : 'false'};
const int  BLE_OPEN_DELAY_SEC  = ${b.bleOpenDelay ?? 0};        // açılmadan əvvəl gecikmə`;

  const handleCopy = () => {
    if (!selected) return;
    navigator.clipboard.writeText(buildFirmwareConfig(selected)).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const isOnline = (() => {
    if (!selected) return false;
    const mins = selected.lastSeenAt ? Math.floor((Date.now() - new Date(selected.lastSeenAt)) / 60000) : null;
    return selected.isOnline && mins !== null && mins < 2;
  })();

  /* ─── RENDER ─── */
  return (
    <>
      <style>{`
        @keyframes bvPulseRed   { 0%{box-shadow:0 0 0 0 rgba(220,38,38,0.6)}  70%{box-shadow:0 0 0 16px rgba(220,38,38,0)}  100%{box-shadow:0 0 0 0 rgba(220,38,38,0)} }
        @keyframes bvPulseGreen { 0%{box-shadow:0 0 0 0 rgba(16,185,129,0.6)} 70%{box-shadow:0 0 0 16px rgba(16,185,129,0)} 100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} }
        @keyframes bvSpin { to{transform:rotate(360deg)} }
        @keyframes bvFade { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        .bv-btn:not(:disabled):hover { filter:brightness(1.18); transform:translate(-50%,-50%) scale(1.08) !important; }
        .bv-btn:not(:disabled):active { transform:translate(-50%,-50%) scale(0.96) !important; }
        .bv-item:hover { border-color:#3b82f644 !important; background:#3b82f608 !important; }
      `}</style>

      <div style={S.root}>

        {/* ══════════ SOL ══════════ */}
        <div style={S.left}>
          <div style={S.leftHead}>
            <div>
              <h1 style={S.title}>{t('bar_title')}</h1>
              <p  style={S.sub}>{barriers.length} {t('bar_devices')}</p>
            </div>
            <button style={S.addBtn} onClick={() => { setShowCreate(v => !v); setForm(EMPTY_FORM); }}>
              {showCreate ? '✕' : '+ ' + t('bar_add')}
            </button>
          </div>

          {showCreate && (
            <form onSubmit={handleCreate} style={S.card}>
              {[[t('bar_name')+' *','name','28 May küç., 14A həyəti'],[t('address'),'address','Bakı, Nərimanov r.']].map(([l,k,p])=>(
                <React.Fragment key={k}>
                  <div style={S.lbl}>{l}</div>
                  <input style={S.inp} placeholder={p} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} required={l.includes('*')}/>
                </React.Fragment>
              ))}
              <div style={S.lbl}>ESP32 MAC</div>
              <MacInput style={S.inp} value={form.deviceSerial} onChange={v=>setForm({...form,deviceSerial:v})}/>
              <div style={S.lbl}>{t('notes')}</div>
              <input style={S.inp} placeholder={t('bar_optional')} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
              <button style={{...S.btn, marginTop:4}} type="submit">{t('bar_add_btn')}</button>
            </form>
          )}

          {loading
            ? <div style={S.hint}>{t('loading')}</div>
            : barriers.length === 0
              ? <div style={S.emptyList}>{t('bar_empty')}</div>
              : barriers.map(b => {
                const sel = selected?.id === b.id;
                const mins = b.lastSeenAt ? Math.floor((Date.now()-new Date(b.lastSeenAt))/60000) : null;
                const on = b.isOnline && mins !== null && mins < 2;
                return (
                  <div key={b.id} className="bv-item"
                    style={{...S.item, ...(sel ? S.itemSel : {})}}
                    onClick={() => openDetail(b)}>
                    <div style={{...S.dot, background: on ? '#10b981' : '#ef4444'}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={S.itemName}>{b.name}</div>
                      <div style={S.itemMeta}>{on ? t('bar_online') : t('bar_offline')}{b.firmwareVersion && ` · v${b.firmwareVersion}`}{b.deviceSerial && ` · ${b.deviceSerial}`}</div>
                    </div>
                    <button style={{...S.miniBtn, opacity: opening===b.id ? 0.4 : 1}}
                      onClick={e=>{e.stopPropagation(); handleOpen(b.id);}}
                      disabled={opening===b.id} title="Aç">
                      {opening===b.id ? '…' : '⚡'}
                    </button>
                  </div>
                );
              })}
        </div>

        {/* ══════════ SAĞ ══════════ */}
        <div style={S.right}>

          {/* ── Yuxarı: detal / boş ── */}
          <div style={S.rightTop}>
            {selected ? (
              <>
                {/* Header */}
                <div style={S.hdr}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={S.hdrName}>{selected.name}</div>
                    <div style={S.hdrAddr}>{selected.address || t('bar_no_address')}</div>
                  </div>
                  {!editMode && (
                    <>
                      <button style={S.btnEdit}  onClick={startEdit}>✎ {t('edit')}</button>
                      <button style={{...S.btnEdit, color:'#f59e0b', background:'#f59e0b15', borderColor:'#f59e0b25'}}
                        onClick={handleOpenCfg}>⚙ {t('bar_config')}</button>
                      <button style={{...S.btnEdit, color: relayTesting ? '#f59e0b' : '#06b6d4', background: relayTesting ? '#f59e0b15' : '#06b6d415', borderColor: relayTesting ? '#f59e0b30' : '#06b6d430'}}
                        onClick={handleRelayTest} disabled={relayTesting} title="Relay test — şlaqbaumu qısa açır">
                        {relayTesting ? `⏱ ${relayCountdown}s` : '🔌 Relay'}
                      </button>
                      <button style={S.btnDel}   onClick={()=>handleDelete(selected.id)}>{t('delete')}</button>
                    </>
                  )}
                  <button style={S.btnClose} onClick={()=>{setSelected(null);setEditMode(false);resetAnim();}}>✕</button>
                </div>

                {/* Status pills */}
                {!editMode && (
                  <div style={S.pills}>
                    {/* Online/Offline */}
                    <span style={{...S.pill, color: isOnline?'#10b981':'#ef4444', background:(isOnline?'#10b981':'#ef4444')+'18', borderColor:(isOnline?'#10b981':'#ef4444')+'40'}}>
                      <span style={{width:7,height:7,borderRadius:'50%',background:'currentColor',flexShrink:0,display:'inline-block',
                        animation: isOnline ? 'bvPulseGreen 2s infinite' : 'none'}}/>
                      {isOnline ? t('bar_online') : t('bar_offline')}
                    </span>
                    {/* Firmware */}
                    {selected.firmwareVersion && <span style={S.pill2}>v{selected.firmwareVersion}</span>}
                    {/* MAC */}
                    {selected.deviceSerial    && <span style={{...S.pill2,color:'#a78bfa'}}>{selected.deviceSerial}</span>}
                    {/* Batareya */}
                    {selected.batteryPercent != null && (
                      <span style={{...S.pill2,
                        color: selected.batteryPercent > 50 ? '#10b981' : selected.batteryPercent > 20 ? '#f59e0b' : '#ef4444',
                        borderColor: selected.batteryPercent > 50 ? '#10b98130' : selected.batteryPercent > 20 ? '#f59e0b30' : '#ef444430',
                      }}>
                        {selected.batteryPercent > 80 ? '🔋' : selected.batteryPercent > 50 ? '🔋' : selected.batteryPercent > 20 ? '🪫' : '🔴'}
                        {' '}{selected.batteryPercent}%
                      </span>
                    )}
                    {/* Elektrik mənbəyi */}
                    {selected.powerStatus && (
                      <span style={{...S.pill2,
                        color: selected.powerStatus === 'ac' ? '#10b981' : '#f59e0b',
                        borderColor: selected.powerStatus === 'ac' ? '#10b98130' : '#f59e0b30',
                      }}>
                        {selected.powerStatus === 'ac' ? '⚡ Şəbəkə' : '🔋 Batareya'}
                      </span>
                    )}
                    {/* Bağlanma rejimi */}
                    <span style={{...S.pill2, color:'#94a3b8'}}>
                      {selected.closeMode === 'sensor' ? '📡 Sensor' : `⏱ ${selected.autoCloseSeconds??4}s`}
                    </span>
                  </div>
                )}

                {editMode ? (
                  <form onSubmit={handleSave} style={S.editForm}>
                    <div style={S.editTitle}>{t('bar_edit_title')}</div>
                    {[[t('bar_name')+' *','name'],[t('address'),'address']].map(([l,k])=>(
                      <React.Fragment key={k}>
                        <div style={S.lbl}>{l}</div>
                        <input style={S.inp} value={editForm[k]} onChange={e=>setEditForm({...editForm,[k]:e.target.value})} required={l.includes('*')}/>
                      </React.Fragment>
                    ))}
                    <div style={S.lbl}>ESP32 MAC</div>
                    <MacInput style={S.inp} value={editForm.deviceSerial} onChange={v=>setEditForm({...editForm,deviceSerial:v})}/>
                    <div style={S.lbl}>{t('notes')}</div>
                    <input style={S.inp} value={editForm.notes} onChange={e=>setEditForm({...editForm,notes:e.target.value})}/>
                    <div style={S.lbl}>{t('bar_close_mode')}</div>
                    <select style={S.inp} value={editForm.closeMode} onChange={e=>setEditForm({...editForm,closeMode:e.target.value})}>
                      <option value="timer">⏱ {t('bar_close_timer')}</option>
                      <option value="sensor">📡 {t('bar_close_sensor')}</option>
                    </select>
                    {editForm.closeMode === 'timer' && (<>
                      <div style={S.lbl}>{t('bar_open_duration')}</div>
                      <input style={S.inp} type="number" min="1" max="60" value={editForm.autoCloseSeconds}
                        onChange={e=>setEditForm({...editForm,autoCloseSeconds:e.target.value})}/>
                    </>)}
                    <div style={{display:'flex',gap:8,marginTop:4}}>
                      <button style={{...S.btn,flex:1}} type="submit" disabled={saving}>{saving?'…':'✓ ' + t('save')}</button>
                      <button style={{...S.btnCancel,flex:1}} type="button" onClick={()=>setEditMode(false)}>{t('cancel')}</button>
                    </div>
                  </form>
                ) : (
                  <>
                    {/* Tabs */}
                    <div style={S.tabs}>
                      {[['logs',t('bar_tab_logs')],['users',t('bar_tab_users')],['info',t('bar_tab_info')],['firmware','🔄 Firmware'],['guestqr','🔑 ' + t('bar_tab_guestqr')]].map(([tabKey,l])=>(
                        <button key={tabKey} style={{...S.tab,...(detailTab===tabKey?S.tabOn:{})}}
                          onClick={()=>{ setDetailTab(tabKey); if(tabKey==='firmware') loadFwInfo(); }}>
                          {l}
                        </button>
                      ))}
                    </div>

                    <div style={S.tabBody}>
                      {/* LOGLAR */}
                      {detailTab==='logs' && (
                        <>
                          {/* Filter + Export toolbar */}
                          <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
                            {['all','internet','bluetooth','guest_qr','call','scheduled','system','manual'].map(f => (
                              <button key={f} style={{
                                padding:'3px 10px',borderRadius:12,fontSize:11,cursor:'pointer',border:'1px solid',fontWeight:600,background:'none',
                                color: logFilter===f ? (METHOD_CFG[f]?.color||'var(--primary)') : 'var(--text3)',
                                borderColor: logFilter===f ? (METHOD_CFG[f]?.color||'var(--primary)')+'60' : 'var(--border)',
                                background: logFilter===f ? (METHOD_CFG[f]?.color||'var(--primary)')+'18' : 'transparent',
                              }} onClick={()=>setLogFilter(f)}>
                                {f==='all'?t('all'):f==='internet'?'🌐 İnternet':f==='bluetooth'?'📶 BT':f==='guest_qr'?'🔑 Qonaq':f==='call'?'📞 Zəng':f==='scheduled'?'⏰ Cədvəl':f==='system'?'⚙️ Sistem':'🖐 Manuel'}
                              </button>
                            ))}
                            <button style={{marginLeft:'auto',padding:'3px 10px',borderRadius:12,fontSize:11,cursor:'pointer',border:'1px solid var(--border)',background:'transparent',color:'#64748b'}}
                              onClick={()=>exportLogsCsv(logs,selected?.name)} title="CSV olaraq yüklə">
                              ⬇ CSV
                            </button>
                          </div>
                          {logsLoading
                            ? <div style={S.hint}>{t('loading')}</div>
                            : (() => {
                                const filtered = logFilter==='all' ? logs : logs.filter(l=>l.method===logFilter);
                                return filtered.length===0
                                  ? <div style={S.emptyList}>{t('bar_no_logs')}</div>
                                  : filtered.map(l=>{
                                    const m=METHOD_CFG[l.method]||METHOD_CFG.internet;
                                    return (
                                      <div key={l.id} style={S.logRow}>
                                        <span style={{...S.logBadge,color:m.color,background:m.color+'18',borderColor:m.color+'30'}}>{m.label}</span>
                                        <div style={{flex:1}}>
                                          <div style={S.logName}>
                                            {l.method==='guest_qr'
                                              ? (l.guestName ? `🔑 ${l.guestName}` : '🔑 Qonaq')
                                              : (l.user?.fullName || 'Admin')}
                                          </div>
                                          {l.method==='guest_qr' && l.user?.fullName && <div style={S.logPhone}>{l.user.fullName} qonağı</div>}
                                          {l.method!=='guest_qr' && l.user?.phone && <div style={S.logPhone}>{l.user.phone}</div>}
                                          {l.failReason && <div style={S.logFail}>{l.failReason}</div>}
                                        </div>
                                        <div style={{textAlign:'right',flexShrink:0}}>
                                          <div style={{fontSize:12,fontWeight:700,color:l.success?'var(--success)':'var(--danger)'}}>{l.success?'✓':'✕'}</div>
                                          <div style={S.logTime}>{fmtShort(l.createdAt)}</div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()
                          }
                        </>
                      )}

                      {/* İSTİFADƏÇİLƏR */}
                      {detailTab==='users' && (
                        <>
                          <form onSubmit={handleAddUser} style={{display:'flex',gap:8,marginBottom:12}}>
                            <input style={{...S.inp,flex:1}} placeholder="+994501234567" value={addPhone} onChange={e=>setAddPhone(e.target.value)}/>
                            <button style={S.btn} type="submit">{t('bar_add_btn')}</button>
                          </form>
                          {usersLoading ? <div style={S.hint}>{t('loading')}</div>
                            : users.length===0 ? <div style={S.emptyList}>{t('bar_no_users')}</div>
                            : users.map(u=>(
                              <div key={u.id} style={S.logRow}>
                                <div style={S.avatar}>{(u.fullName?.[0]||'?').toUpperCase()}</div>
                                <div style={{flex:1}}><div style={S.logName}>{u.fullName}</div><div style={S.logPhone}>{u.phone}</div></div>
                                <button style={S.btnRm} onClick={()=>handleRemoveUser(u.id)}>{t('bar_remove')}</button>
                              </div>
                            ))
                          }
                        </>
                      )}

                      {/* MƏLUMAT */}
                      {detailTab==='info' && (
                        <>
                          {[
                            ['ID',                    selected.id,          true],
                            [t('bar_name'),           selected.name,        false],
                            [t('address'),            selected.address||'—', false],
                            ['MAC',                   selected.deviceSerial||t('bar_not_set'), true],
                            [t('bar_ip'),             selected.localIp||'—', true],
                            [t('bar_sim_phone'),      selected.simPhone||'—', false],
                            ['SIM ICCID',             selected.simIccid||'—', true],
                            ['MQTT Topic',            selected.mqttTopic,   true],
                            ['Firmware',              selected.firmwareVersion ? `v${selected.firmwareVersion}` : '—', false],
                            [t('bar_close_mode'),     selected.closeMode==='sensor' ? `📡 ${t('bar_close_sensor')}` : `⏱ ${t('bar_close_timer')} (${selected.autoCloseSeconds??4}s)`, false],
                            [t('bar_servo_angle'),    `${t('bar_open')}: ${selected.servoOpenAngle??90}°  /  ${t('bar_closed')}: ${selected.servoClosedAngle??0}°`, false],
                            [t('bar_servo_speed'),    selected.servoSpeed===0||selected.servoSpeed==null ? t('bar_instant') : `${selected.servoSpeed}ms/°`, false],
                            ['BLE Bluetooth',         selected.bleEnabled ? `✓ Aktiv — ${selected.bleRssiThreshold??-70}dBm / ${selected.bleScanDuration??5}s${selected.bleAutoOpen?' / Avtomatik':''}` : 'Deaktiv', false],
                            [t('bar_battery'),        selected.batteryPercent != null ? `${selected.batteryPercent}%` : '—', false],
                            ['WiFi RSSI',             selected.wifiRSSI != null ? `${selected.wifiRSSI} dBm` : '—', false],
                            [t('bar_power'),          selected.powerStatus === 'ac' ? `⚡ ${t('bar_power_ac')}` : selected.powerStatus === 'battery' ? `🔋 ${t('bar_power_bat')}` : '—', false],
                            [t('bar_last_seen'),      fmtDT(selected.lastSeenAt), false],
                            [t('bar_created'),        fmtDate(selected.createdAt), false],
                          ].map(([k,v,mono])=>(
                            <div key={k} style={S.infoRow}>
                              <span style={S.infoK}>{k}</span>
                              <span style={{...S.infoV, fontFamily: mono ? 'monospace' : 'inherit'}}>{v}</span>
                            </div>
                          ))}
                          <div style={{marginTop:20}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                              <span style={{fontSize:11,color:'#475569',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Firmware Config</span>
                              <button style={{...S.btn,padding:'4px 12px',fontSize:11}} onClick={handleCopy}>{copied?'✓ ' + t('bar_copied') : t('bar_copy')}</button>
                            </div>
                            <pre style={S.code}>{buildFirmwareConfig(selected)}</pre>
                          </div>
                        </>
                      )}

                      {/* FIRMWARE / OTA */}
                      {detailTab==='firmware' && (
                        <div style={{padding:'16px 0'}}>

                          {/* ── Cihazda mövcud versiya ── */}
                          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,
                            padding:'10px 14px',background:'#060d1c',borderRadius:8,border:'1px solid #1a2d48'}}>
                            <div style={{flex:1}}>
                              <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Cihazda</div>
                              <div style={{fontSize:18,fontWeight:800,color:'var(--text)',marginTop:2}}>
                                {selected.firmwareVersion ? `v${selected.firmwareVersion}` : '—'}
                              </div>
                            </div>
                            <div style={{fontSize:24}}>📟</div>
                            <div style={{flex:1,textAlign:'right'}}>
                              <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Serverdə</div>
                              <div style={{fontSize:18,fontWeight:800,color: fwInfo ? 'var(--warning)' : '#334155',marginTop:2}}>
                                {fwInfo ? `v${fwInfo.version}` : 'Yüklənməyib'}
                              </div>
                              {fwInfo && <div style={{fontSize:10,color:'#334155'}}>{formatDate(fwInfo.uploadedAt, 'datetimesec')}</div>}
                            </div>
                          </div>

                          {/* ── Firmware yüklə ── */}
                          <div style={{marginBottom:14}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>
                              📤 Firmware Yüklə
                            </div>

                            {/* Versiya sahəsi */}
                            <input style={{...S.inp,marginBottom:8,fontSize:12}}
                              placeholder="Versiya (məs: 3.5.0) — boş buraxsanız avtomatik tapılır"
                              value={fwVersion}
                              onChange={e=>setFwVersion(e.target.value)}/>

                            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                              {/* Lokal qovluqdan yüklə — TÖVSIYƏ OLUNAN */}
                              <button style={{...S.btn,flex:1,padding:'9px 14px',fontSize:12,
                                background:'linear-gradient(135deg,#10b981,#059669)',
                                opacity: fwUploading ? 0.6 : 1, cursor: fwUploading ? 'not-allowed' : 'pointer'}}
                                onClick={handleLoadLocal} disabled={fwUploading}>
                                {fwUploading ? '⏳...' : '📂 Lokal qovluqdan yüklə'}
                              </button>

                              {/* Manuel fayl seçimi — alternativ */}
                              <input ref={fwFileRef} type="file" accept=".bin"
                                style={{display:'none'}} id="fw-file-inp"
                                onChange={e => {
                                  const f = e.target.files?.[0];
                                  if (f && !fwVersion) {
                                    const m = f.name.match(/(\d+\.\d+\.\d+)/);
                                    if (m) setFwVersion(m[1]);
                                  }
                                }}/>
                              <label htmlFor="fw-file-inp" style={{...S.btnCancel,padding:'9px 14px',cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',gap:5}}>
                                📁 {fwFileRef.current?.files?.[0]?.name || '.bin seç'}
                              </label>
                              <button style={{...S.btn,padding:'9px 14px',fontSize:12,
                                opacity: fwUploading ? 0.6 : 1, cursor: fwUploading ? 'not-allowed' : 'pointer'}}
                                onClick={handleFwUpload} disabled={fwUploading}>
                                ⬆
                              </button>
                            </div>

                            <div style={{fontSize:10,color:'#334155',marginTop:6}}>
                              📂 Lokal: Arduino IDE → Sketch → <b>Export Compiled Binary</b> → qovluqda <code style={{color:'#64748b'}}>.bin</code> yaranır → burada düyməyə bas
                            </div>
                          </div>

                          {/* ── OTA trigger ── */}
                          <div style={{marginBottom:14,padding:'12px 14px',background:'#060d1c',borderRadius:8,border:'1px solid #1a2d48'}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>
                              ⚡ Uzaqdan Firmware Yenilə (OTA)
                            </div>
                            <div style={{fontSize:11,color:'var(--text3)',marginBottom:12,lineHeight:1.6}}>
                              Cihaz MQTT üzərindən OTA koandası alır → firmware URL-dən endirilib flash-a yazılır → avtomatik restart.
                              {!selected.isOnline && <span style={{color:'#ef4444',marginLeft:6}}>⚠ Cihaz offline!</span>}
                            </div>
                            <button style={{...S.btn,width:'100%',padding:'10px',fontSize:13,
                              background: otaActive ? '#0f1e30' : fwInfo ? 'linear-gradient(135deg,#8b5cf6,#6d28d9)' : '#1a2d48',
                              opacity: (otaActive || !fwInfo) ? 0.7 : 1,
                              cursor: (otaActive || !fwInfo) ? 'not-allowed' : 'pointer'}}
                              onClick={handleTriggerOta}
                              disabled={otaActive || !fwInfo}>
                              {otaActive ? '📡 OTA gedir...' : fwInfo ? `🚀 OTA Göndər (v${fwInfo.version})` : '⚠ Əvvəlcə firmware yükləyin'}
                            </button>
                          </div>

                          {/* ── OTA progress ── */}
                          {(otaProgress > 0 || otaStatus) && (
                            <div style={{marginTop:8}}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                                <span style={{fontSize:11,
                                  color: otaStatus.startsWith('✅') ? 'var(--success)'
                                       : otaStatus.startsWith('❌') ? 'var(--danger)'
                                       : otaStatus.startsWith('⏱') ? 'var(--warning)'
                                       : '#93c5fd'}}>
                                  {otaStatus}
                                </span>
                                {otaProgress > 0 && (
                                  <span style={{fontSize:11,fontWeight:800,fontFamily:'monospace',
                                    color: otaProgress===100 ? 'var(--success)'
                                         : otaActive ? 'var(--warning)' : '#93c5fd'}}>
                                    {otaProgress}%{otaActive && otaProgress < 86 ? ' ≈' : ''}
                                  </span>
                                )}
                              </div>
                              {otaProgress > 0 && (
                                <div style={{background:'#060d1c',borderRadius:20,height:8,overflow:'hidden',border:'1px solid #1a2d48'}}>
                                  <div style={{
                                    height:'100%', borderRadius:20,
                                    width: otaProgress + '%',
                                    background: otaProgress===100
                                      ? 'linear-gradient(90deg,#10b981,#059669)'
                                      : otaStatus.startsWith('❌') ? 'var(--danger)'
                                      : otaStatus.startsWith('⏱') ? 'var(--warning)'
                                        : 'linear-gradient(90deg,#8b5cf6,#a855f7,#f59e0b)',
                                    transition: 'width 0.8s ease',
                                    boxShadow: otaActive && otaProgress < 100 ? '0 0 8px #8b5cf6' : 'none',
                                  }}/>
                                </div>
                              )}
                            </div>
                          )}

                        </div>
                      )}

                      {/* QONAQ QR */}
                      {detailTab==='guestqr' && (
                        <GuestQrTab barrierId={selected?.id} S={S} />
                      )}

                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={S.emptyState}>
                <div style={S.emptyIcon}>⊞</div>
                <div style={S.emptyMsg}>{t('bar_select')}</div>
                <div style={S.emptySub}>{t('bar_select_sub')}</div>
              </div>
            )}
          </div>

          {/* ══ AŞAĞI: Animasiyalı şlaqbaum — həmişə görünür ══ */}
          <div style={S.vizBox}>
            {/* SVG fill */}
            <BarrierVisual isOpen={barrierOpen} online={isOnline}/>

            {/* Top-left: barrier name label when selected */}
            {selected && (
              <div style={S.vizLabel}>
                <span style={{...S.dot2, background: isOnline ? '#10b981' : '#ef4444'}}/>
                {selected.name}
              </div>
            )}

            {/* Bottom gradient fade */}
            <div style={S.vizFade}/>

            {/* ── Dairəvi açma/bağlama düyməsi ── */}
            {selected && !editMode && (
              <>
                <button
                  className="bv-btn"
                  disabled={barrierOpen}
                  onClick={() => !barrierOpen && handleOpen(selected.id)}
                  title={barrierOpen ? 'Bağlanır...' : 'Şlaqbaumu aç'}
                  style={{
                    position: 'absolute',
                    left: '50%', bottom: 28,
                    transform: 'translate(-50%, 0)',
                    width: 58, height: 58,
                    borderRadius: '50%',
                    border: `3px solid ${barrierOpen ? '#10b981' : (isOnline ? '#dc2626' : '#374151')}`,
                    background: barrierOpen
                      ? 'radial-gradient(circle at 40% 35%, #34d399, #059669)'
                      : isOnline
                        ? 'radial-gradient(circle at 40% 35%, #f87171, #b91c1c)'
                        : 'radial-gradient(circle at 40% 35%, #4b5563, #1f2937)',
                    cursor: barrierOpen || !isOnline ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, color: 'white',
                    boxShadow: barrierOpen
                      ? '0 0 0 0 transparent, 0 4px 24px rgba(16,185,129,0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                      : isOnline
                        ? '0 0 0 0 transparent, 0 4px 24px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.15)'
                        : '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                    transition: 'all 0.35s',
                    animation: !barrierOpen && isOnline ? 'bvPulseRed 2.4s infinite' : barrierOpen ? 'bvPulseGreen 2.4s infinite' : 'none',
                    backdropFilter: 'blur(2px)',
                    zIndex: 10,
                  }}>
                  {barrierOpen ? '↕' : '⚡'}
                </button>

                {/* Countdown badge */}
                {barrierOpen && countdown > 0 && (
                  <div style={{
                    position: 'absolute', bottom: 93, left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(16,185,129,0.15)',
                    border: '1px solid rgba(16,185,129,0.35)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 20, padding: '3px 12px',
                    fontSize: 12, fontWeight: 700, color: '#34d399',
                    whiteSpace: 'nowrap', zIndex: 10,
                    animation: 'bvFade 0.3s ease',
                  }}>
                    {t('bar_closing')} — {countdown}s
                  </div>
                )}
              </>
            )}

            {/* No barrier selected hint */}
            {!selected && (
              <div style={{
                position: 'absolute', bottom: 20, left: '50%',
                transform: 'translateX(-50%)',
                color: '#1e3a5f', fontSize: 11,
                whiteSpace: 'nowrap',
              }}>
                {t('bar_select_hint')}
              </div>
            )}
          </div>

        </div>{/* /right */}
      </div>

      {/* ════════ KONFİQ MODAL OVERLAY ════════ */}
      {showCfg && cfgSnap && (
        <div style={S.overlay} onClick={()=>{ if(!cfgSending) setShowCfg(false); }}>
          <div style={S.modal} onClick={e=>e.stopPropagation()}>

            {/* ── Modal başlıq ── */}
            <div style={S.modalHdr}>
              <div>
                <div style={{fontSize:15,fontWeight:800,color:'var(--text)'}}>⚙ {t('bar_device_config')}</div>
                <div style={{display:'flex',gap:8,alignItems:'center',marginTop:4}}>
                  <span style={{fontSize:11,color:'#64748b'}}>{cfgSnap.name}</span>
                  {cfgSnap.deviceSerial && <span style={{fontSize:10,fontFamily:'monospace',color:'var(--text3)',background:'#060d1c',padding:'1px 6px',borderRadius:4}}>{cfgSnap.deviceSerial}</span>}
                  <span style={{fontSize:10,padding:'1px 8px',borderRadius:10,fontWeight:700,
                    background: cfgSnap.isOnline?'#10b98115':'#ef444415',
                    color: cfgSnap.isOnline?'#10b981':'#ef4444'}}>
                    {cfgSnap.isOnline ? t('bar_online') : t('bar_offline')}
                  </span>
                  {cfgSnap.lastSeenAt && <span style={{fontSize:10,color:'#334155'}}>Son: {fmtShort(cfgSnap.lastSeenAt)}</span>}
                </div>
              </div>
              <button style={S.btnClose} onClick={()=>{ if(!cfgSending) setShowCfg(false); }}>✕</button>
            </div>

            {/* ── Forma ── */}
            <form onSubmit={handleSendConfig} style={{overflowY:'auto',flex:1,padding:'8px 20px 20px'}}>

              {/* ══ BÖLMƏ: WiFi ══ */}
              <div style={S.modalSec}>📡 WiFi Şəbəkəsi</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>SSID (Şəbəkə adı)</div>
                  <input style={S.inp} value={cfgForm.wifiSsid} autoComplete="off"
                    placeholder={cfgSnap.lastKnownSsid || 'Mövcudu saxla'}
                    onChange={e=>setCfgForm(f=>({...f,wifiSsid:e.target.value}))}/>
                  <div style={S.cfgHint}>
                    Cihazda: <b>{cfgSnap.lastKnownSsid || '—'}</b>
                    {cfgSnap.lastKnownConnMode && <span style={{marginLeft:6,color:'var(--primary)'}}>{cfgSnap.lastKnownConnMode}</span>}
                  </div>
                </div>
                <div>
                  <div style={S.lbl}>WiFi Şifrəsi</div>
                  <input style={S.inp} value={cfgForm.wifiPassword} autoComplete="new-password"
                    placeholder="Boş saxla = dəyişmə"
                    onChange={e=>setCfgForm(f=>({...f,wifiPassword:e.target.value}))}/>
                  <div style={S.cfgHint}>
                    Cihazda: <b>{cfgSnap.lastKnownWifiPass ? '••••••' + cfgSnap.lastKnownWifiPass.slice(-2) : '—'}</b>
                    {cfgSnap.lastKnownWifiPass && <span style={{marginLeft:6,color:'var(--text3)',cursor:'pointer',fontSize:10}}
                      onClick={()=>setCfgForm(f=>({...f,wifiPassword:cfgSnap.lastKnownWifiPass}))}>
                      (göstər)
                    </span>}
                  </div>
                </div>
              </div>

              {/* ══ BÖLMƏ: Server ══ */}
              <div style={S.modalSec}>🖥 Server Bağlantısı</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>Server IP / Host</div>
                  <input style={S.inp} value={cfgForm.serverHost}
                    placeholder={cfgSnap.lastKnownServerHost || '192.168.0.129'}
                    onChange={e=>setCfgForm(f=>({...f,serverHost:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownServerHost || '—'}</b></div>
                </div>
                <div>
                  <div style={S.lbl}>Port</div>
                  <input style={S.inp} type="number" value={cfgForm.serverPort}
                    onChange={e=>setCfgForm(f=>({...f,serverPort:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: 3000</div>
                </div>
              </div>

              {/* ══ BÖLMƏ: Şlaqbaum ══ */}
              <div style={S.modalSec}>🚧 Şlaqbaum Davranışı</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>Bağlanma rejimi</div>
                  <select style={S.inp} value={cfgForm.closeMode}
                    onChange={e=>setCfgForm(f=>({...f,closeMode:e.target.value}))}>
                    <option value="timer">⏱ Taymer</option>
                    <option value="sensor">📡 Sensor</option>
                  </select>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.closeMode==='sensor'?'Sensor':'Taymer'}</b></div>
                </div>
                <div>
                  <div style={S.lbl}>Taymer (saniyə)</div>
                  <input style={S.inp} type="number" min="1" max="300"
                    value={cfgForm.autoCloseSeconds}
                    disabled={cfgForm.closeMode==='sensor'}
                    onChange={e=>setCfgForm(f=>({...f,autoCloseSeconds:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.autoCloseSeconds ?? 4}s</b></div>
                </div>
                <div>
                  <div style={S.lbl}>Heartbeat intervalı (saniyə)</div>
                  <input style={S.inp} type="number" min="5" max="300"
                    value={cfgForm.heartbeatMs}
                    onChange={e=>setCfgForm(f=>({...f,heartbeatMs:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownHeartbeatMs ? cfgSnap.lastKnownHeartbeatMs/1000+'s' : '30s'}</b></div>
                </div>
                <div>
                  <div style={S.lbl}>Sensor timeout (saniyə)</div>
                  <input style={S.inp} type="number" min="10" max="600"
                    value={cfgForm.sensorTimeoutMs}
                    disabled={cfgForm.closeMode==='timer'}
                    onChange={e=>setCfgForm(f=>({...f,sensorTimeoutMs:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownSensorTimeout ? cfgSnap.lastKnownSensorTimeout/1000+'s' : '60s'}</b></div>
                </div>
              </div>

              {/* ══ BÖLMƏ: GSM ══ */}
              <div style={S.modalSec}>📶 GSM / 4G (SIM Kart)</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>APN</div>
                  <input style={S.inp} value={cfgForm.gsmApn}
                    placeholder="internet"
                    onChange={e=>setCfgForm(f=>({...f,gsmApn:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownGsmApn || 'internet'}</b></div>
                </div>
                <div>
                  <div style={S.lbl}>APN İstifadəçi (opsional)</div>
                  <input style={S.inp} value={cfgForm.gsmUser} placeholder="Boş qoy"
                    onChange={e=>setCfgForm(f=>({...f,gsmUser:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownGsmUser || '—'}</b></div>
                </div>
                <div>
                  <div style={S.lbl}>APN Şifrə (opsional)</div>
                  <input style={S.inp} value={cfgForm.gsmPass} placeholder="Boş qoy"
                    onChange={e=>setCfgForm(f=>({...f,gsmPass:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.lastKnownGsmPass || '—'}</b></div>
                </div>
                <div style={{display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                  <div style={S.lbl}>GSM Siqnal (CSQ)</div>
                  <div style={{...S.inp, color:'var(--text2)', cursor:'default', display:'flex', alignItems:'center', gap:6}}>
                    {cfgSnap.gsmCsq != null ? (
                      <>
                        <span style={{color: cfgSnap.gsmCsq>15?'var(--success)':cfgSnap.gsmCsq>8?'var(--warning)':'var(--danger)', fontWeight:700}}>{cfgSnap.gsmCsq}</span>
                        <span style={{color:'var(--text3)',fontSize:11}}>{cfgSnap.gsmCsq>15?'Yaxşı':cfgSnap.gsmCsq>8?'Orta':'Zəif'}</span>
                      </>
                    ) : '—'}
                  </div>
                  <div style={S.cfgHint}>Salt oxu — cihazdan</div>
                </div>
              </div>

              {/* ══ BÖLMƏ: SIM Kart & Şəbəkə ══ */}
              <div style={S.modalSec}>📱 SIM Kart & Şəbəkə</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>SIM Telefon Nömrəsi</div>
                  <input style={S.inp} value={cfgForm.simPhone}
                    placeholder="+994501234567"
                    onChange={e=>setCfgForm(f=>({...f,simPhone:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.simPhone || '—'}</b>  ← Admin daxil edir</div>
                </div>
                <div>
                  <div style={S.lbl}>SIM ICCID (serial)</div>
                  <div style={{...S.inp, color:'#64748b', cursor:'default', fontFamily:'monospace', fontSize:11, display:'flex', alignItems:'center'}}>
                    {cfgSnap.simIccid || '—'}
                  </div>
                  <div style={S.cfgHint}>Cihazdan avtomatik oxunur (4G aktiv olduqda)</div>
                </div>
                <div>
                  <div style={S.lbl}>Cihaz IP Ünvanı</div>
                  <div style={{...S.inp, color: cfgSnap.localIp ? '#10b981' : '#334155', cursor:'default', fontFamily:'monospace', display:'flex', alignItems:'center', gap:6}}>
                    {cfgSnap.localIp
                      ? <><span style={{width:6,height:6,borderRadius:'50%',background:'#10b981',flexShrink:0,display:'inline-block'}}/>
                          {cfgSnap.localIp}</>
                      : '—'}
                  </div>
                  <div style={S.cfgHint}>
                    {cfgSnap.lastKnownConnMode === '4G' ? '4G IP · ' : 'WiFi IP · '}
                    WiFi RSSI: <b>{cfgSnap.wifiRSSI != null ? cfgSnap.wifiRSSI+'dBm' : '—'}</b>
                  </div>
                </div>
                <div>
                  <div style={S.lbl}>GSM Siqnal (CSQ)</div>
                  <div style={{...S.inp, color:'var(--text2)', cursor:'default', display:'flex', alignItems:'center', gap:6}}>
                    {cfgSnap.gsmCsq != null ? (
                      <>
                        <span style={{color: cfgSnap.gsmCsq>15?'var(--success)':cfgSnap.gsmCsq>8?'var(--warning)':'var(--danger)', fontWeight:700}}>{cfgSnap.gsmCsq}</span>
                        <span style={{color:'var(--text3)',fontSize:11}}>{cfgSnap.gsmCsq>15?'Yaxşı':cfgSnap.gsmCsq>8?'Orta':'Zəif'}</span>
                        <span style={{color:'#1e3a5f',fontSize:10,marginLeft:'auto'}}>{cfgSnap.lastKnownConnMode||''}</span>
                      </>
                    ) : '—'}
                  </div>
                  <div style={S.cfgHint}>Salt oxu — cihazdan heartbeat ilə gəlir</div>
                </div>
              </div>

              {/* ══ BÖLMƏ: Servo Motor ══ */}
              <div style={S.modalSec}>🔧 Servo Motor Ayarları</div>
              <div style={S.modalGrid}>
                <div>
                  <div style={S.lbl}>Açılma Bucağı (°)</div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <input style={{...S.inp,flex:1}} type="number" min="0" max="180"
                      value={cfgForm.servoOpen}
                      onChange={e=>setCfgForm(f=>({...f,servoOpen:e.target.value}))}/>
                    <div style={{width:36,height:36,borderRadius:8,background:'var(--primary)15',border:'1px solid var(--primary)30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'var(--primary)',flexShrink:0}}>
                      {cfgForm.servoOpen || 90}°
                    </div>
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.servoOpenAngle ?? 90}°</b>  (0–180°)</div>
                </div>
                <div>
                  <div style={S.lbl}>Bağlanma Bucağı (°)</div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <input style={{...S.inp,flex:1}} type="number" min="0" max="180"
                      value={cfgForm.servoClosed}
                      onChange={e=>setCfgForm(f=>({...f,servoClosed:e.target.value}))}/>
                    <div style={{width:36,height:36,borderRadius:8,background:'var(--danger)15',border:'1px solid var(--danger)30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'var(--danger)',flexShrink:0}}>
                      {cfgForm.servoClosed !== '' ? cfgForm.servoClosed : 0}°
                    </div>
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.servoClosedAngle ?? 0}°</b>  (0–180°)</div>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <div style={S.lbl}>Hərəkət Sürəti</div>
                  <div style={{display:'flex',gap:8,marginTop:4}}>
                    {[
                      ['0',  '⚡ Ani',         '0ms — dərhal hərəkət edir'],
                      ['5',  '🏃 Sürətli',      '5ms/° ≈ 0.5s'],
                      ['15', '🚶 Orta',          '15ms/° ≈ 1.4s'],
                      ['30', '🐌 Yavaş',         '30ms/° ≈ 2.7s'],
                    ].map(([val,label,hint])=>(
                      <button key={val} type="button"
                        style={{flex:1,padding:'8px 4px',borderRadius:8,border:`1.5px solid ${cfgForm.servoSpeed===val?'#f59e0b':'#1a2d48'}`,background:cfgForm.servoSpeed===val?'#f59e0b15':'#091525',cursor:'pointer',transition:'all 0.15s'}}
                        onClick={()=>setCfgForm(f=>({...f,servoSpeed:val}))}>
                        <div style={{fontSize:13}}>{label.split(' ')[0]}</div>
                        <div style={{fontSize:11,fontWeight:700,color:cfgForm.servoSpeed===val?'#f59e0b':'#64748b',marginTop:2}}>{label.split(' ').slice(1).join(' ')}</div>
                        <div style={{fontSize:9,color:'#334155',marginTop:2}}>{hint}</div>
                      </button>
                    ))}
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.servoSpeed===0?'Ani':cfgSnap.servoSpeed<=5?'Sürətli':cfgSnap.servoSpeed<=15?'Orta':'Yavaş'}</b> ({cfgSnap.servoSpeed??0}ms/°)</div>
                </div>
              </div>

              {/* ══ BÖLMƏ: Bluetooth (BLE) ══ */}
              <div style={S.modalSec}>📶 Bluetooth (BLE) Ayarları</div>
              <div style={S.modalGrid}>
                {/* BLE master toggle */}
                <div style={{gridColumn:'1/-1'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'#060d1c',borderRadius:8,padding:'10px 14px',border:`1.5px solid ${cfgForm.bleEnabled?'#3b82f6':'#1a2d48'}`,transition:'all 0.2s'}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>BLE Bluetooth Girişi</div>
                      <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>Telefon ilə Bluetooth üzərindən qapı açma funksiyası</div>
                    </div>
                    <button type="button"
                      style={{width:48,height:26,borderRadius:13,border:'none',cursor:'pointer',position:'relative',
                        background: cfgForm.bleEnabled ? '#3b82f6' : '#1a2d48', transition:'background 0.2s'}}
                      onClick={()=>setCfgForm(f=>({...f,bleEnabled:!f.bleEnabled}))}>
                      <div style={{position:'absolute',top:3,left:cfgForm.bleEnabled?24:4,width:20,height:20,borderRadius:10,background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}}/>
                    </button>
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.bleEnabled ? 'Aktiv ✓' : 'Deaktiv'}</b></div>
                </div>

                {/* RSSI Threshold */}
                <div style={{gridColumn:'1/-1',opacity: cfgForm.bleEnabled ? 1 : 0.4, pointerEvents: cfgForm.bleEnabled ? 'auto' : 'none'}}>
                  <div style={S.lbl}>Yaxınlıq Həddi (RSSI)</div>
                  <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap'}}>
                    {[
                      ['-90','🌐 ~25m','Çox uzaq','Bina girişi'],
                      ['-80','📡 ~12m','Uzaq','Həyət girişi'],
                      ['-70','📶 ~6m','Orta (tövsiyə)','Normal istifadə'],
                      ['-60','📳 ~2m','Yaxın','Dar keçidlər'],
                      ['-50','🤝 ~0.5m','Çox yaxın','Sıx güvənlik'],
                    ].map(([val,icon,label,hint])=>(
                      <button key={val} type="button"
                        style={{flex:'1 1 80px',padding:'8px 4px',borderRadius:8,border:`1.5px solid ${cfgForm.bleRssiThreshold===val?'#3b82f6':'#1a2d48'}`,background:cfgForm.bleRssiThreshold===val?'#3b82f615':'#060d1c',cursor:'pointer',transition:'all 0.15s',minWidth:72}}
                        onClick={()=>setCfgForm(f=>({...f,bleRssiThreshold:val}))}>
                        <div style={{fontSize:14}}>{icon.split(' ')[0]}</div>
                        <div style={{fontSize:10,fontWeight:700,color:cfgForm.bleRssiThreshold===val?'#3b82f6':'#64748b',marginTop:1}}>{label}</div>
                        <div style={{fontSize:9,color:'#334155',marginTop:1}}>{hint}</div>
                        <div style={{fontSize:9,color:'#1e3a5f',marginTop:1,fontFamily:'monospace'}}>{val} dBm</div>
                      </button>
                    ))}
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.bleRssiThreshold ?? -70} dBm</b> — aşağı dəyər = daha uzaq məsafə</div>
                </div>

                {/* Scan Duration */}
                <div style={{opacity: cfgForm.bleEnabled ? 1 : 0.4, pointerEvents: cfgForm.bleEnabled ? 'auto' : 'none'}}>
                  <div style={S.lbl}>Skan Müddəti (san)</div>
                  <input style={S.inp} type="number" min="3" max="30" value={cfgForm.bleScanDuration}
                    onChange={e=>setCfgForm(f=>({...f,bleScanDuration:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.bleScanDuration ?? 5}s</b> (3–30)</div>
                </div>

                {/* Open Delay */}
                <div style={{opacity: cfgForm.bleEnabled ? 1 : 0.4, pointerEvents: cfgForm.bleEnabled ? 'auto' : 'none'}}>
                  <div style={S.lbl}>Açılma Gecikmə (san)</div>
                  <input style={S.inp} type="number" min="0" max="10" value={cfgForm.bleOpenDelay}
                    onChange={e=>setCfgForm(f=>({...f,bleOpenDelay:e.target.value}))}/>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.bleOpenDelay ?? 0}s</b> (0–10)</div>
                </div>

                {/* Auto Open */}
                <div style={{gridColumn:'1/-1',opacity: cfgForm.bleEnabled ? 1 : 0.4, pointerEvents: cfgForm.bleEnabled ? 'auto' : 'none'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'#060d1c',borderRadius:8,padding:'8px 12px',border:'1px solid #1a2d48'}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:'var(--text)'}}>Avtomatik Açılma</div>
                      <div style={{fontSize:10,color:'var(--text2)',marginTop:1}}>Telefon yaxınlaşanda düymə basmadan açılsın</div>
                    </div>
                    <button type="button"
                      style={{width:44,height:24,borderRadius:12,border:'none',cursor:'pointer',position:'relative',
                        background: cfgForm.bleAutoOpen ? '#3b82f6' : '#1a2d48', transition:'background 0.2s'}}
                      onClick={()=>setCfgForm(f=>({...f,bleAutoOpen:!f.bleAutoOpen}))}>
                      <div style={{position:'absolute',top:2,left:cfgForm.bleAutoOpen?22:2,width:20,height:20,borderRadius:10,background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}}/>
                    </button>
                  </div>
                  <div style={S.cfgHint}>Cihazda: <b>{cfgSnap.bleAutoOpen ? 'Avtomatik ✓' : 'Manuel (düymə lazım)'}</b></div>
                </div>
              </div>

              {/* Visual servo preview */}
              <div style={{background:'#060d1c',borderRadius:8,padding:'10px 14px',border:'1px solid #1a2d48',marginTop:4,display:'flex',alignItems:'center',gap:16}}>
                <div style={{fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,flexShrink:0}}>Motor vəziyyəti</div>
                <div style={{flex:1,display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:11,color:'#64748b',flexShrink:0}}>Bağlı {cfgForm.servoClosed || 0}°</span>
                  <div style={{flex:1,height:4,background:'#1a2d48',borderRadius:4,position:'relative'}}>
                    <div style={{position:'absolute',left:Math.min(parseInt(cfgForm.servoClosed||0),parseInt(cfgForm.servoOpen||90))/180*100+'%',width:Math.abs(parseInt(cfgForm.servoOpen||90)-parseInt(cfgForm.servoClosed||0))/180*100+'%',height:'100%',background:'linear-gradient(90deg,#ef4444,#3b82f6)',borderRadius:4}}/>
                    <div style={{position:'absolute',left:parseInt(cfgForm.servoClosed||0)/180*100+'%',transform:'translateX(-50%)',top:-3,width:10,height:10,borderRadius:'50%',background:'var(--danger)',border:'2px solid #fff'}}/>
                    <div style={{position:'absolute',left:parseInt(cfgForm.servoOpen||90)/180*100+'%',transform:'translateX(-50%)',top:-3,width:10,height:10,borderRadius:'50%',background:'var(--primary)',border:'2px solid #fff'}}/>
                  </div>
                  <span style={{fontSize:11,color:'#64748b',flexShrink:0}}>Açıq {cfgForm.servoOpen || 90}°</span>
                  <span style={{fontSize:10,color:'#1e3a5f',flexShrink:0}}>/ 180°</span>
                </div>
              </div>

              {/* ══ BÖLMƏ: Cihaz Məlumatları (salt oxu) ══ */}
              <div style={S.modalSec}>📊 Cihaz Məlumatları (salt oxu)</div>
              <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'6px 12px',
                background:'#060d1c', borderRadius:8, padding:'10px 12px', border:'1px solid #1a2d48'}}>
                {[
                  ['Board',      cfgSnap.lastKnownBoard     || 'LILYGO-T-SIM7600G-H'],
                  ['Firmware',   cfgSnap.firmwareVersion ? 'v'+cfgSnap.firmwareVersion : '—'],
                  ['MAC',        cfgSnap.deviceSerial        || '—'],
                  ['Batareya',   cfgSnap.batteryPercent != null ? cfgSnap.batteryPercent+'%' : '—'],
                  ['Şarj',       cfgSnap.isCharging ? 'Bəli ⚡' : cfgSnap.isCharging===false ? 'Xeyr' : '—'],
                  ['Elektrik',   cfgSnap.powerStatus==='ac' ? '⚡ Şəbəkə' : cfgSnap.powerStatus==='battery' ? '🔋 Batareya' : '—'],
                  ['Uptime',     cfgSnap.lastKnownUptime != null ? Math.floor(cfgSnap.lastKnownUptime/60)+'d '+cfgSnap.lastKnownUptime%60+'s' : '—'],
                  ['Bağlantı',   cfgSnap.lastKnownConnMode || '—'],
                  ['WiFi RSSI',  cfgSnap.wifiRSSI != null ? cfgSnap.wifiRSSI+'dBm' : '—'],
                ].map(([k,v])=>(
                  <div key={k}>
                    <div style={{fontSize:9,color:'#334155',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>{k}</div>
                    <div style={{fontSize:11,color:'var(--text2)',fontFamily:'monospace',marginTop:1}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* ══ PROGRESS BAR ══ */}
              {cfgProgress > 0 && (
                <div style={{marginTop:14}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <span style={{fontSize:11,color: cfgProgress===100?'var(--success)':cfgStatus.startsWith('❌')?'var(--danger)':'#93c5fd'}}>
                      {cfgStatus}
                    </span>
                    <span style={{fontSize:11,fontWeight:800,fontFamily:'monospace',
                      color: cfgProgress===100?'var(--success)':cfgStatus.startsWith('❌')?'var(--danger)':'#93c5fd'}}>
                      {cfgProgress}%
                    </span>
                  </div>
                  <div style={{background:'#060d1c',borderRadius:20,height:6,overflow:'hidden',border:'1px solid #1a2d48'}}>
                    <div style={{
                      height:'100%', borderRadius:20,
                      width: cfgProgress+'%',
                      background: cfgProgress===100
                        ? 'linear-gradient(90deg,#10b981,#059669)'
                        : cfgStatus.startsWith('❌')
                          ? 'var(--danger)'
                          : 'linear-gradient(90deg,#3b82f6,#f59e0b)',
                      transition: 'width 0.6s ease, background 0.3s',
                    }}/>
                  </div>
                </div>
              )}

              <div style={{fontSize:10,color:'#334155',margin:'10px 0 14px'}}>
                ⚠ WiFi / Server dəyişikliyi cihazı yenidən başladacaq. Boş sahələr mövcud dəyəri saxlayır.
              </div>

              <div style={{display:'flex',gap:10}}>
                <button style={{...S.btn, flex:1, padding:'11px', fontSize:13,
                  background: cfgSending&&cfgProgress<100 ? '#0f1e30' : 'linear-gradient(135deg,#f59e0b,#d97706)',
                  opacity: cfgSending&&cfgProgress<100 ? 0.65 : 1,
                  cursor: cfgSending&&cfgProgress<100 ? 'not-allowed':'pointer'}}
                  type="submit" disabled={cfgSending&&cfgProgress<100}>
                  {cfgSending && cfgProgress<100 ? '📡 Göndərilir...' : cfgProgress===100 ? '✅ Yenidən Göndər' : '⚡ Cihaza Göndər'}
                </button>
                <button style={{...S.btnCancel, padding:'11px 20px', fontSize:13}}
                  type="button" onClick={()=>setShowCfg(false)}>
                  {cfgSending&&cfgProgress<100 ? t('back') : t('close')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   STİLLƏR
───────────────────────────────────────────────────────────── */
const S = {
  /* Layout */
  root:  { display:'grid', gridTemplateColumns:'340px 1fr', gap:20, height:'calc(100vh - 64px)', alignItems:'stretch' },
  left:  { display:'flex', flexDirection:'column', gap:8, overflowY:'auto', paddingRight:2 },
  right: { background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 },

  /* Right inner */
  rightTop: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 },

  /* Viz box — fixed, at bottom */
  vizBox: { position:'relative', height:260, flexShrink:0, background:'#060d1c', borderTop:'2px solid var(--border)', overflow:'hidden' },
  vizLabel: { position:'absolute', top:10, left:14, display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.55)', zIndex:5, textShadow:'0 1px 6px #000', letterSpacing:'0.3px' },
  dot2:  { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  vizFade: { position:'absolute', bottom:0, left:0, right:0, height:60, background:'linear-gradient(transparent, #060d1c55)', pointerEvents:'none', zIndex:4 },

  /* Left panel */
  leftHead:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', paddingBottom:4 },
  title:     { fontSize:20, fontWeight:800, color:'var(--text)', marginBottom:2 },
  sub:       { color:'var(--text3)', fontSize:12 },
  addBtn:    { background:'linear-gradient(135deg, var(--primary), #06b6d4)', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer', flexShrink:0, letterSpacing:'0.2px' },

  card:      { background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, padding:14, display:'flex', flexDirection:'column', gap:7 },
  lbl:       { fontSize:10, color:'var(--text3)', fontWeight:700, textTransform:'uppercase', letterSpacing:0.6 },
  inp:       { background:'var(--input-bg)', border:'1px solid var(--border)', borderRadius:7, padding:'8px 11px', color:'var(--text)', fontSize:12, outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'monospace', transition:'border-color 0.2s' },
  btn:       { background:'linear-gradient(135deg, var(--primary), #2563eb)', color:'#fff', border:'none', borderRadius:7, padding:'8px 14px', fontWeight:700, cursor:'pointer', fontSize:12, whiteSpace:'nowrap', letterSpacing:'0.2px' },
  btnCancel: { background:'var(--bg2)', color:'var(--text3)', border:'1px solid var(--border)', borderRadius:7, padding:'8px 14px', fontWeight:600, cursor:'pointer', fontSize:12 },
  hint:      { textAlign:'center', color:'var(--text3)', padding:24, fontSize:12 },
  emptyList: { textAlign:'center', color:'var(--text3)', padding:24, background:'var(--bg2)', borderRadius:10, border:'1px dashed var(--border)', fontSize:12 },

  item:    { display:'flex', alignItems:'center', gap:10, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 12px', cursor:'pointer', transition:'all 0.2s' },
  itemSel: { borderColor:'var(--primary)', background:'var(--nav-active-bg)' },
  dot:     { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  itemName: { fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  itemMeta: { fontSize:10, color:'var(--text3)' },
  miniBtn:  { background:'var(--success)15', border:'none', borderRadius:6, padding:'3px 9px', color:'var(--success)', fontWeight:700, cursor:'pointer', fontSize:13, flexShrink:0 },

  /* Right header */
  hdr:      { display:'flex', alignItems:'center', gap:8, padding:'13px 18px', borderBottom:'1px solid var(--border)' },
  hdrName:  { fontSize:15, fontWeight:800, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  hdrAddr:  { fontSize:11, color:'var(--text3)', marginTop:1 },
  btnEdit:  { background:'var(--primary)15', color:'var(--primary)', border:'1px solid var(--primary)25', borderRadius:7, padding:'6px 11px', fontWeight:600, cursor:'pointer', fontSize:11, whiteSpace:'nowrap', flexShrink:0 },
  btnDel:   { background:'var(--danger)15', color:'var(--danger)', border:'1px solid var(--danger)25', borderRadius:7, padding:'6px 11px', fontWeight:600, cursor:'pointer', fontSize:11, flexShrink:0 },
  btnClose: { background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:17, padding:'4px 6px', flexShrink:0 },

  pills:    { display:'flex', gap:6, padding:'9px 18px', borderBottom:'1px solid var(--border)', flexWrap:'wrap', alignItems:'center' },
  pill:     { fontSize:13, fontWeight:700, padding:'4px 12px', borderRadius:20, display:'flex', alignItems:'center', gap:5, border:'1px solid transparent' },
  pill2:    { fontSize:12, color:'var(--text2)', background:'var(--bg3)', padding:'4px 12px', borderRadius:20, fontFamily:'monospace', border:'1px solid var(--border)' },

  editForm:  { padding:18, display:'flex', flexDirection:'column', gap:8, overflowY:'auto', flex:1, minHeight:0 },
  editTitle: { fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:2 },

  tabs:    { display:'flex', borderBottom:'1px solid var(--border)' },
  tab:     { flex:1, padding:'10px', background:'none', border:'none', color:'var(--text3)', fontSize:12, fontWeight:700, cursor:'pointer', letterSpacing:'0.2px', transition:'color 0.15s' },
  tabOn:   { color:'var(--primary)', borderBottom:'2px solid var(--primary)' },
  tabBody: { padding:'14px 16px', overflowY:'auto', flex:1, minHeight:0 },

  logRow:   { display:'flex', alignItems:'flex-start', gap:10, padding:'9px 0', borderBottom:'1px solid var(--border)' },
  logBadge: { fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap', flexShrink:0, border:'1px solid transparent' },
  logName:  { fontSize:12, fontWeight:600, color:'var(--text)' },
  logPhone: { fontSize:10, color:'var(--text3)' },
  logFail:  { fontSize:10, color:'var(--danger)', marginTop:2 },
  logTime:  { fontSize:10, color:'var(--text3)', marginTop:1, fontFamily:'monospace' },
  avatar:   { width:32, height:32, borderRadius:'50%', background:'var(--primary)15', color:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0, border:'1px solid var(--primary)25' },
  btnRm:    { background:'var(--danger)10', color:'var(--danger)', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:11, fontWeight:600, flexShrink:0 },

  infoRow:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'10px 0', borderBottom:'1px solid var(--border)' },
  infoK:    { fontSize:12, color:'var(--text3)', fontWeight:700, flexShrink:0, letterSpacing:'0.2px' },
  infoV:    { fontSize:13, color:'var(--text)', wordBreak:'break-all', maxWidth:'65%', textAlign:'right', lineHeight:1.4 },
  code:     { background:'var(--bg)', border:'1px solid var(--border)', borderRadius:7, padding:12, color:'var(--success)', fontSize:10, fontFamily:'monospace', overflowX:'auto', whiteSpace:'pre-wrap', lineHeight:1.8 },

  /* Empty state (right panel, no selection) */
  emptyState: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6 },
  emptyIcon:  { fontSize:44, opacity:0.1, color:'var(--text)' },
  emptyMsg:   { color:'var(--text3)', fontSize:14, fontWeight:600 },
  emptySub:   { color:'var(--text3)', fontSize:11, opacity:0.6 },

  /* Config Modal Overlay */
  overlay: {
    position:'fixed', inset:0, zIndex:1000,
    background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)',
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:20,
  },
  modal: {
    background:'var(--card)', border:'1px solid var(--border)',
    borderRadius:14, width:'100%', maxWidth:640,
    maxHeight:'90vh', display:'flex', flexDirection:'column',
    boxShadow:'0 24px 80px rgba(0,0,0,0.6)',
    overflow:'hidden',
  },
  modalHdr: {
    display:'flex', justifyContent:'space-between', alignItems:'flex-start',
    padding:'18px 20px 14px', borderBottom:'1px solid var(--border)', flexShrink:0,
  },
  modalSec: {
    fontSize:11, fontWeight:800, color:'var(--warning)', letterSpacing:'0.8px',
    textTransform:'uppercase', marginTop:16, marginBottom:6,
  },
  modalGrid: {
    display:'grid', gridTemplateColumns:'1fr 1fr', gap:10,
  },
  cfgHint: {
    fontSize:10, color:'var(--text3)', marginTop:3, fontFamily:'monospace',
  },
};
